import { getEmailProblem, isValidEmailStrict } from '@/utils/email';
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchCEPInfo } from "@/components/event-simulator/utils/apiService";
import { linkAnonymousSimulationByIdToUser } from "@/services/simulationService";
import { claimSharedQuote } from "@/utils/claimSharedQuote";
import {
  buildJourneyStepKeys,
  resolveInitialJourneyStep,
  type JourneyStepCompletion,
  type JourneyStepKey,
} from "@/utils/infosFestaSteps";
import Navbar from "@/components/Navbar";
import SupplierJourneyHeader from "@/components/supplier-store/SupplierJourneyHeader";
import { StoreNavTabs } from "@/components/supplier-store/StoreNavTabs";
import { getSupplierTheme } from "@/utils/supplierCustomizationCache";
import Footer from "@/components/Footer";
import SupplierFooter from "@/components/supplier-store/SupplierFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { MapPin, PartyPopper, Mail, Lock, ArrowLeft, Clock, Truck, User, Package, Calendar, Home, Timer, Loader2, Phone, CreditCard, ShoppingCart, Cake, ChefHat, UtensilsCrossed, Church, Coffee, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuditFields } from "@/hooks/useAuditFields";
import { useFormProgression } from "@/components/event-simulator/hooks/useFormProgression";
import { convertToOptimizedStructure, normalizeSelectedSuppliers } from "@/utils/supplierJsonOptimizer";
import { addBusinessDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { calculateDeliveryDate, formatDateForStorage, parseDateFromStorage } from "@/utils/dateUtils";
import { calculateEndTimeWithDayOverflow } from "@/utils/endTimeCalculation";
import { formatArrivalLead } from "@/utils/formatEventDuration";
import { useAmeUpTax } from "@/hooks/useAmeUpTax";
import { usePlatformTax } from "@/hooks/usePlatformTax";
import { fetchCoverageRowsForContext } from '@/utils/deliveryContext';
import { calculateFreightForEvent } from "@/utils/calculateDeliveryFee";
import { fetchTollQuote } from "@/utils/tollService";
import { calculateCouponDiscount, fetchShippingCoupon } from "@/utils/coupons";
import { formatMonetaryValue } from "@/utils/persistSimulation";
import { logDebug, logInfo, logError } from "@/utils/logger";
import { getSupplierColors, saveSupplierCustomization, loadSupplierCustomization } from "@/utils/supplierCustomizationCache";
import { useSupplierAvailability } from "@/hooks/useSupplierAvailability";
import { useStoreManagement } from "@/hooks/useStoreManagement";
import { checkDeliveryAreaCoverage, type CepRange, type DeliveryValidationResult } from "@/utils/cepUtils";
import { normalizeRegionCode } from '@/utils/brazilianStates';
import { parseStateRegistration } from '@/utils/stateRegistration';
import { ClientStateRegistrationField } from '@/components/shared/ClientStateRegistrationField';
import { useSupplierFavicon } from '@/hooks/useSupplierFavicon';
import { getHomeFallbackPath } from '@/utils/whiteLabelStore';
import { isRealEventType, resolveEventTypeForSave, splitStoredEventType, formatCustomEventType } from '@/utils/eventType';

/** Map a DB row to a CepRange */
function rowToCepRange(row: any): CepRange {
  return {
    id: row.id,
    region: row.region_name || '',
    type: Array.isArray(row.celulas) && row.celulas.length > 0 ? 'cells' : ((row.area_type as 'cep' | 'circle') || 'cep'),
    start: row.cep_start || undefined,
    end: row.cep_end || undefined,
    center: row.latitude != null && row.longitude != null
      ? [Number(row.latitude), Number(row.longitude)]
      : undefined,
    cells: Array.isArray(row.celulas) ? row.celulas : [],
    cellsResolution: row.cells_resolution ?? 7,
    context: (row.context as any) || 'event',
    perKmEnabled: row.per_km_enabled ?? false,
    perKmRate: row.per_km_rate != null ? Number(row.per_km_rate) : 0,
    perKmBaseFee: row.per_km_base_fee != null ? Number(row.per_km_base_fee) : 0,
    perKmMinFee: row.per_km_min_fee != null ? Number(row.per_km_min_fee) : 0,
    radiusMeters: row.radius_meters ?? undefined,
    deliveryFee: row.delivery_fee != null ? Number(row.delivery_fee) : 0,
    freeDelivery: row.free_delivery ?? false,
    isDisabled: row.is_disabled ?? false,
    deliveryTimeMinutes: row.delivery_time_minutes ?? 30,
    allowPickup: row.allow_pickup ?? false,
    eventSizeSurchargeEnabled: row.event_size_surcharge_enabled ?? false,
    eventSizeSurcharges: Array.isArray(row.event_size_surcharges)
      ? row.event_size_surcharges.map((s: any) => ({
          minGuests: s.min_guests,
          maxGuests: s.max_guests,
          surcharge: s.surcharge,
        }))
      : [],
    // Faixas de distância: sem elas o aviso de frete usava sempre o valor base da região (ex.: R$ 6,90) enquanto o
    // carrinho e o checkout aplicavam a faixa certa (ex.: grátis).
    distanceRangesEnabled: row.distance_ranges_enabled ?? false,
    distanceRanges: Array.isArray(row.distance_ranges)
      ? row.distance_ranges.map((r: any) => ({
          minKm: Number(r.minKm) || 0,
          maxKm: Number(r.maxKm) || 0,
          deliveryFee: Number(r.deliveryFee) || 0,
          freeDelivery: r.freeDelivery ?? false,
        }))
      : [],
  };
}

// Helper functions (placeholders for the real implementation)
const formatCepForStorage = (cep: string) => cep.replace(/\D/g, '');
const isValidUuid = (value?: string | null) => !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const sanitizeDurationValue = (value?: string | null) => {
  if (!value) return '';
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) {
    return '';
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const parseJsonIfPossible = (value: any) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const normalizeDurationFromHours = (value: any) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 24) {
    return `${String(Math.trunc(value)).padStart(2, '0')}:00`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const normalizedTime = sanitizeDurationValue(trimmed);
    if (normalizedTime) {
      return normalizedTime;
    }

    if (!trimmed.startsWith('-') && /^\d{1,2}$/.test(trimmed)) {
      const hours = Number(trimmed);
      if (hours > 0 && hours <= 24) {
        return `${String(hours).padStart(2, '0')}:00`;
      }
    }
  }

  return '';
};

const extractDurationFromStructuredData = (source: any) => {
  const root = parseJsonIfPossible(source);
  if (!root || typeof root !== 'object') return '';

  const stack = Array.isArray(root) ? [...root] : [root];

  while (stack.length > 0) {
    const current = parseJsonIfPossible(stack.shift());
    if (!current || typeof current !== 'object') continue;

    const durationFromHourTime = normalizeDurationFromHours((current as any).event_hour_time);
    if (durationFromHourTime) return durationFromHourTime;

    Object.values(current).forEach((child) => {
      const parsedChild = parseJsonIfPossible(child);
      if (parsedChild && typeof parsedChild === 'object') {
        stack.push(parsedChild);
      }
    });
  }

  return '';
};

const resolveStoredEventDuration = (eventLike: any) => {
  if (!eventLike || typeof eventLike !== 'object') return '';

  return (
    sanitizeDurationValue(eventLike.party_time) ||
    extractDurationFromStructuredData(eventLike.delivery_supplier) ||
    extractDurationFromStructuredData(eventLike.menu)
  );
};

const sanitizeArrivalOffset = (value?: string | null) => {
  if (!value) return '';
  const trimmed = value.trim();
  const absoluteMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (absoluteMatch) {
    const hours = Number(absoluteMatch[1]);
    const minutes = Number(absoluteMatch[2]);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 ? trimmed : '';
  }
  const relativeMatch = trimmed.match(/(\d+\s*h\s*\d*|\d+\s*min)/i);
  return relativeMatch ? relativeMatch[0].replace(/\s+/g, ' ').trim() : '';
};

// Debounce helper
const useDebouncedCallback = (callback: (...args: any[]) => void, delay: number) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback((...args: any[]) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);
};

const InfosFesta = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLDivElement>(null);
  const {
    user,
    signIn,
    signUp,
    signInWithProvider,
    saveSimulationData
  } = useAuth();
  const {
    getAuditFieldsForInsert,
    getAuditFieldsForUpdate
  } = useAuditFields();
  const [loading, setLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingDeliveryCep, setLoadingDeliveryCep] = useState(false);
  const simulationId = searchParams.get('simulationId');
  const menuTypeParam = searchParams.get('menuType');
  const [supplierIdState, setSupplierIdState] = useState<string | null>(null);
  const [inferredMenuType, setInferredMenuType] = useState<string | null>(menuTypeParam);
  const requireLogin = searchParams.get('requireLogin') === 'true';
  const eventIdParam = searchParams.get('eventId');
  const [eventCode, setEventCode] = useState<string | null>(null);

  // ── Jornada do Cliente: passos ocultáveis (Passo 1 Evento / Passo 3 Data e Horário) ──
  // Precedência: skipSteps da URL > flags do cardápio > customização da loja > tudo visível.
  const urlSkipSteps = useMemo(() => {
    const skip = searchParams.get('skipSteps');
    return skip ? skip.split(',').map(s => s.trim()).filter(Boolean) : null;
  }, [searchParams]);

  const [journeyConfig, setJourneyConfig] = useState<{
    showStepEvent: boolean;
    showStepDatetime: boolean;
    eventLocationLabel: string | null;
    eventDateLabel: string | null;
  } | null>(null);

  useEffect(() => {
    // Se a URL já define os passos, não precisamos consultar o banco.
    if (urlSkipSteps) return;

    const eventRef = isValidUuid(eventIdParam) ? eventIdParam : (isValidUuid(simulationId) ? simulationId : null);
    if (!eventRef) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Evento (pai) → fornecedor + nome do cardápio
        const { data: parent } = await (supabase as any)
          .from('events')
          .select('id, supplier_id, menu, menu_types')
          .eq('id', eventRef)
          .maybeSingle();
        
        // Infer menuType from menu_types column if available
        if (parent?.menu_types && parent.menu_types.length > 0) {
          if (parent.menu_types.includes('selecao') && !parent.menu_types.includes('fixo')) {
            setInferredMenuType('selecao');
          } else {
            setInferredMenuType('evento');
          }
        }

        let supplierId: string | null = parent?.supplier_id ?? null;
        let menuName: string | null = typeof parent?.menu === 'string' ? parent.menu : null;

        // Eventos-pai frequentemente não têm supplier_id/menu: buscar no filho
        if (!supplierId || !menuName) {
          const { data: child } = await (supabase as any)
            .from('events')
            .select('supplier_id, menu')
            .eq('parent_event_id', eventRef)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();
          supplierId = supplierId || child?.supplier_id || null;
          menuName = menuName || (typeof child?.menu === 'string' ? child.menu : null);
        }

        if (!supplierId || cancelled) return;
        setSupplierIdState(supplierId);

        // 2) Flags do cardápio específico (quando identificável pelo nome)
        if (menuName) {
          const { data: menuRow } = await (supabase as any)
            .from('supplier_menu')
            .select('show_step_event, show_step_datetime, event_location_label, event_date_label')
            .eq('supplier_id', supplierId)
            .eq('name', menuName)
            .is('parent_menu_id', null)
            .limit(1)
            .maybeSingle();

          if (menuRow && !cancelled) {
            setJourneyConfig({
              showStepEvent: menuRow.show_step_event ?? true,
              showStepDatetime: menuRow.show_step_datetime ?? true,
              eventLocationLabel: menuRow.event_location_label ?? null,
              eventDateLabel: menuRow.event_date_label ?? null,
            });
            return;
          }
        }

        // 3) Fallback: customização da loja
        const { data: custom } = await (supabase as any)
          .from('supplier_store_customization')
          .select('show_step_event, show_step_datetime, event_location_label, event_date_label')
          .eq('supplier_id', supplierId)
          .maybeSingle();

        if (custom && !cancelled) {
          setJourneyConfig({
            showStepEvent: custom.show_step_event ?? true,
            showStepDatetime: custom.show_step_datetime ?? true,
            eventLocationLabel: custom.event_location_label ?? null,
            eventDateLabel: custom.event_date_label ?? null,
          });
        }
      } catch (err) {
        console.warn('[InfosFesta] Não foi possível resolver a Jornada do Cliente:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [urlSkipSteps, eventIdParam, simulationId]);

  // Tipo de evento já informado no orçamento → o passo 1 não precisa ser exibido.
  const [eventTypeAlreadySetFromCache] = useState<boolean>(() => {
    try {
      if (isRealEventType(sessionStorage.getItem('ameup_event_type'))) return true;
      // Fallback: o tipo pode estar apenas nos itens do carrinho (jornada da loja).
      const raw = sessionStorage.getItem('supplier_cart_items');
      if (!raw) return false;
      const items = JSON.parse(raw);
      return Array.isArray(items) && items.some((item: any) => isRealEventType(item?.eventType));
    } catch {
      return false;
    }
  });
  const [tipoEvento, setTipoEvento] = useState("");
  // Quando o tipo é "Outros", o cliente descreve qual é o evento (obrigatório).
  const [tipoEventoOutros, setTipoEventoOutros] = useState<string>(() => {
    try { return sessionStorage.getItem('ameup_event_type_other') || ''; } catch { return ''; }
  });
  const updateTipoEventoOutros = (value: string) => {
    setTipoEventoOutros(value);
    try {
      if (value.trim()) sessionStorage.setItem('ameup_event_type_other', value);
      else sessionStorage.removeItem('ameup_event_type_other');
    } catch { /* ignore */ }
  };
  const outrosPendente = tipoEvento === 'Outros' && !tipoEventoOutros.trim();

  // Pedido com cardápio fixo (sozinho ou junto com seleção): a jornada é SEMPRE a de eventos.
  // Passos escondidos vêm só dos produtos fixos — nunca do link nem dos produtos de seleção.
  const [eventHasFixo, setEventHasFixo] = useState(false);
  // Proposta montada pelo fornecedor: o frete combinado nela vale (o aviso da tela não pode divergir do carrinho).
  const [proposalDeliveryFee, setProposalDeliveryFee] = useState<number | null>(null);
  const [fixoShowStepEvent, setFixoShowStepEvent] = useState(true);
  useEffect(() => {
    const eventRef = isValidUuid(eventIdParam) ? eventIdParam : (isValidUuid(simulationId) ? simulationId : null);
    if (!eventRef) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: parent } = await (supabase as any)
          .from('events')
          .select('supplier_id, menu_types, event_type')
          .eq('id', eventRef)
          .maybeSingle();
        // Tipo de evento já definido no pedido/proposta (ex.: pelo fornecedor): o cliente não precisa escolher de novo.
        // Lido aqui, logo na abertura, para não depender do carregamento completo dos dados do formulário.
        if (!cancelled && isRealEventType(parent?.event_type)) {
          const stored = splitStoredEventType(parent.event_type as string);
          setTipoEvento((prev) => prev || stored.tipo);
          if (stored.outros) updateTipoEventoOutros(stored.outros);
          setEventTypeFromDb(true);
        }
        const types: string[] = Array.isArray(parent?.menu_types) ? parent.menu_types : [];
        if (!types.includes('fixo')) {
          if (!cancelled) setEventHasFixo(false);
          return;
        }
        if (cancelled) return;
        setEventHasFixo(true);

        // Opção "mostrar passo Evento" dos produtos FIXOS do pedido (some só se todos desligarem).
        const { data: children } = await (supabase as any)
          .from('events')
          .select('origem, supplier_id')
          .eq('parent_event_id', eventRef)
          .eq('is_active', true);
        const productIds = ((children || []) as Array<{ origem: string | null }>)
          .map((ch) => ch.origem)
          .filter((id): id is string => !!id && isValidUuid(id));
        if (productIds.length === 0) return;
        const { data: menus } = await (supabase as any)
          .from('supplier_menu')
          .select('menu_type, show_step_event')
          .in('id', productIds);
        const fixoMenus = ((menus || []) as Array<{ menu_type: string | null; show_step_event: boolean | null }>)
          .filter((m) => m.menu_type === 'fixo');
        if (!cancelled && fixoMenus.length > 0) {
          setFixoShowStepEvent(fixoMenus.some((m) => m.show_step_event !== false));
        }
      } catch (err) {
        console.warn('[InfosFesta] Não foi possível identificar o cardápio fixo do pedido:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [eventIdParam, simulationId]);
  /**
   * Tipo de evento que JÁ existia quando a página abriu (vindo do banco).
   * Nunca reflete o que o cliente acabou de escolher no Passo 1 — caso
   * contrário o passo desapareceria no exato momento da seleção.
   */
  const [eventTypeFromDb, setEventTypeFromDb] = useState(false);

  const skipSteps = useMemo(() => {
    // Pedido com cardápio fixo: ignora o skipSteps do link e as opções dos produtos de seleção.
    // Data e horário (passo 3) SEMPRE aparecem; o tipo (passo 1) só some se for um tipo real.
    if (eventHasFixo) {
      const fixoBase: string[] = [];
      if (!fixoShowStepEvent) fixoBase.push('1');
      if (eventTypeAlreadySetFromCache || eventTypeFromDb) fixoBase.push('1');
      return Array.from(new Set(fixoBase));
    }
    const base = urlSkipSteps
      ? [...urlSkipSteps]
      : (() => {
          const skip: string[] = [];
          if (journeyConfig?.showStepEvent === false) skip.push('1');
          if (journeyConfig?.showStepDatetime === false) skip.push('3');
          return skip;
        })();

    // Apenas fontes "congeladas" (cache do orçamento ou dado já persistido)
    // podem ocultar o Passo 1. O estado vivo do formulário não entra aqui.
    if ((eventTypeAlreadySetFromCache || eventTypeFromDb) && !base.includes('1')) base.push('1');
    return base;
  }, [urlSkipSteps, journeyConfig, eventTypeAlreadySetFromCache, eventTypeFromDb, eventHasFixo, fixoShowStepEvent]);

  const hideStepEvent = skipSteps.includes('1');
  const hideStepDatetime = skipSteps.includes('3');

  const journeyLocationLabel = journeyConfig?.eventLocationLabel || null;
  const journeyDateLabel = journeyConfig?.eventDateLabel || null;


  // Festa form state
  const [festaNome, setFestaNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [cep, setCep] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [detalhes, setDetalhes] = useState("");
  const pickupRequested = searchParams.get('pickup') === 'true';
  const [isPickup, setIsPickup] = useState(pickupRequested);

  // Endereço de faturamento (nota fiscal) — nem sempre é o mesmo da festa.
  // Por padrão usa o endereço da festa (checkbox marcada); ao desmarcar, o
  // cliente informa um endereço próprio pra nota, começando só pelo CEP.
  const [billingUseEventAddress, setBillingUseEventAddress] = useState(true);
  const [billingCep, setBillingCep] = useState("");
  const [billingEndereco, setBillingEndereco] = useState("");
  const [billingBairro, setBillingBairro] = useState("");
  const [billingCidade, setBillingCidade] = useState("");
  const [billingEstado, setBillingEstado] = useState("");
  const [billingNumero, setBillingNumero] = useState("");
  const [billingComplemento, setBillingComplemento] = useState("");
  const [loadingBillingCep, setLoadingBillingCep] = useState(false);
  

  // ========== AUTOSAVE HELPERS ==========
  // Resolve the target event ID for all autosave operations
  const targetEventId = useMemo(() => {
    if (isValidUuid(eventIdParam)) return eventIdParam;
    if (isValidUuid(simulationId)) return simulationId;
    return null;
  }, [eventIdParam, simulationId]);

  // A URL registra a escolha feita no carrinho. Se o evento ainda estiver com
  // o valor antigo, preserva imediatamente a retirada e repara a persistência.
  useEffect(() => {
    if (!pickupRequested || !targetEventId) return;
    setIsPickup(true);
    void supabase
      .from('events')
      .update({ is_pickup: true })
      .eq('id', targetEventId)
      .then(({ error }) => {
        if (error) console.warn('[InfosFesta] Não foi possível reparar is_pickup:', error);
      });
  }, [pickupRequested, targetEventId]);

  // Save a partial update to the `events` table
  // Vários campos eram gravados ao mesmo tempo (um PATCH por campo, em paralelo): as gravações disputavam a mesma
  // linha, estouravam o tempo e apareciam como "Falha ao salvar". Agora os campos pendentes se juntam em UMA
  // gravação por vez, com uma nova tentativa, e o aviso só aparece se a gravação falhar de verdade.
  const pendingPatchRef = useRef<Record<string, any>>({});
  const patchInFlightRef = useRef<Promise<void> | null>(null);
  const lastPatchToastRef = useRef(0);
  const saveEventPatch = useCallback(async (patch: Record<string, any>) => {
    if (!targetEventId) {
      console.warn('⚠️ [Autosave] No valid targetEventId, skipping saveEventPatch', patch);
      return;
    }
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };

    const flush = async () => {
      while (Object.keys(pendingPatchRef.current).length > 0) {
        const batch = pendingPatchRef.current;
        pendingPatchRef.current = {};
        let lastError: any = null;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const { error } = await supabase
              .from('events')
              .update({ ...batch, updated_at: new Date().toISOString() })
              .eq('id', targetEventId);
            lastError = error;
            if (!error) break;
          } catch (err) {
            lastError = err;
          }
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
        if (lastError) {
          console.error('❌ [Autosave] saveEventPatch failed:', lastError, batch);
          if (Date.now() - lastPatchToastRef.current > 8000) {
            lastPatchToastRef.current = Date.now();
            toast.error('Não conseguimos salvar suas informações agora. Confira sua conexão e tente de novo.');
          }
        }
      }
    };

    if (!patchInFlightRef.current) {
      patchInFlightRef.current = flush().finally(() => {
        patchInFlightRef.current = null;
      });
    }
    return patchInFlightRef.current;
  }, [targetEventId]);

  // Upsert a partial update to the `event_location` table.
  // Serialized via a per-component queue to avoid race conditions where
  // concurrent calls each find no existing row and all INSERT, producing
  // duplicate fragmented rows for the same event_id.
  const locationUpsertQueueRef = useRef<Promise<void>>(Promise.resolve());
  const upsertEventLocationPatch = useCallback(async (patch: Record<string, any>) => {
    if (!targetEventId) {
      console.warn('⚠️ [Autosave] No valid targetEventId, skipping upsertEventLocationPatch', patch);
      return;
    }
    const eventId = targetEventId;
    const run = async () => {
      try {
        // Fetch ALL rows for this event_id (legacy data may have duplicates).
        const { data: rows } = await supabase
          .from('event_location')
          .select('id, address_location, address_number, adress_complement, address_code, "Adress_neighborhood", address_city, address_state')
          .eq('event_id', eventId);

        if (rows && rows.length > 0) {
          // Merge existing fragmented rows into a single payload, then apply patch on top.
          const merged: Record<string, any> = {};
          for (const r of rows) {
            for (const [k, v] of Object.entries(r)) {
              if (k === 'id') continue;
              if (v !== null && v !== undefined && v !== '' && (merged[k] === undefined || merged[k] === null || merged[k] === '')) {
                merged[k] = v;
              }
            }
          }
          const finalPayload = { ...merged, ...patch };
          const keepId = rows[0].id;
          const { error } = await supabase
            .from('event_location')
            .update(finalPayload)
            .eq('id', keepId);
          if (error) {
            console.warn('⚠️ [Autosave] event_location update RLS-denied, falling back to edge function', error);
            const { error: fnErr } = await supabase.functions.invoke('upsert-event-location', {
              body: { event_id: eventId, patch }
            });
            if (fnErr) {
              console.error('❌ [Autosave] edge fallback failed:', fnErr, patch);
              toast.error(`Falha ao salvar endereço: ${Object.keys(patch).join(', ')}`);
              return;
            }
          }
          // Remove duplicates, keeping only the canonical row.
          if (rows.length > 1) {
            const dupIds = rows.slice(1).map(r => r.id);
            await supabase.from('event_location').delete().in('id', dupIds);

          }
        } else {
          const { error } = await supabase
            .from('event_location')
            .insert({ id: crypto.randomUUID(), event_id: eventId, ...patch });
          if (error) {
            console.warn('⚠️ [Autosave] event_location insert RLS-denied, falling back to edge function', error);
            const { error: fnErr } = await supabase.functions.invoke('upsert-event-location', {
              body: { event_id: eventId, patch }
            });
            if (fnErr) {
              console.error('❌ [Autosave] edge fallback failed:', fnErr, patch);
              toast.error(`Falha ao salvar endereço: ${Object.keys(patch).join(', ')}`);
            }
          }
        }
      } catch (err) {
        console.error('❌ [Autosave] upsertEventLocationPatch exception:', err);
      }
    };
    // Chain onto the queue so all upserts for this component are sequential.
    const next = locationUpsertQueueRef.current.then(run, run);
    locationUpsertQueueRef.current = next;
    return next;
  }, [targetEventId]);

  // Debounced version for text inputs (address fields)
  const debouncedSaveEventPatch = useDebouncedCallback((patch: Record<string, any>) => {
    saveEventPatch(patch);
  }, 500);

  const debouncedUpsertLocationPatch = useDebouncedCallback((patch: Record<string, any>) => {
    upsertEventLocationPatch(patch);
  }, 500);

  // Upsert em event_billing_address — tabela nova, sem o histórico de linhas
  // fragmentadas de event_location, então um upsert simples por event_id basta.
  const billingUpsertQueueRef = useRef<Promise<void>>(Promise.resolve());
  const upsertBillingAddressPatch = useCallback((patch: Record<string, any>) => {
    if (!targetEventId) {
      console.warn('⚠️ [Autosave] No valid targetEventId, skipping upsertBillingAddressPatch', patch);
      return Promise.resolve();
    }
    const eventId = targetEventId;
    const run = async () => {
      try {
        const { error } = await supabase
          .from('event_billing_address')
          .upsert(
            { event_id: eventId, ...patch, updated_at: new Date().toISOString() },
            { onConflict: 'event_id' },
          );
        if (error) {
          // Cliente anônimo (link compartilhado, sem login): RLS nega a escrita direta
          // porque auth.uid() não bate com o dono do evento — mesmo caso de
          // upsertEventLocationPatch, mesma saída (edge function com service role).
          console.warn('⚠️ [Autosave] event_billing_address RLS-denied, falling back to edge function', error);
          const { error: fnErr } = await supabase.functions.invoke('upsert-event-billing-address', {
            body: { event_id: eventId, patch },
          });
          if (fnErr) {
            console.error('❌ [Autosave] edge fallback failed:', fnErr, patch);
            toast.error('Falha ao salvar endereço de faturamento');
          }
        }
      } catch (err) {
        console.error('❌ [Autosave] upsertBillingAddressPatch exception:', err);
      }
    };
    const next = billingUpsertQueueRef.current.then(run, run);
    billingUpsertQueueRef.current = next;
    return next;
  }, [targetEventId]);

  const debouncedUpsertBillingPatch = useDebouncedCallback((patch: Record<string, any>) => {
    upsertBillingAddressPatch(patch);
  }, 500);
  // ========== END AUTOSAVE HELPERS ==========

  // Carrega o endereço de faturamento já salvo (se existir) assim que o
  // evento é resolvido. Sem linha salva, mantém o padrão (usar endereço da festa).
  useEffect(() => {
    if (!targetEventId) return;
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('event_billing_address')
        .select('use_event_address, address_code, address_location, address_number, address_complement, address_neighborhood, address_city, address_state')
        .eq('event_id', targetEventId)
        .maybeSingle();
      if (!active || error || !data) return;
      setBillingUseEventAddress(data.use_event_address ?? true);
      if (data.address_code) {
        const digits = data.address_code.replace(/\D/g, '');
        setBillingCep(digits.replace(/^(\d{5})(\d)/, '$1-$2'));
      }
      if (data.address_location) setBillingEndereco(data.address_location);
      if (data.address_neighborhood) setBillingBairro(data.address_neighborhood);
      if (data.address_city) setBillingCidade(data.address_city);
      if (data.address_state) setBillingEstado(data.address_state);
      if (data.address_number) setBillingNumero(data.address_number);
      if (data.address_complement) setBillingComplemento(data.address_complement);
    })();
    return () => { active = false; };
  }, [targetEventId]);

  const handleBillingUseEventAddressChange = (checked: boolean) => {
    setBillingUseEventAddress(checked);
    upsertBillingAddressPatch({ use_event_address: checked });
  };

  const handleBillingCepChange = async (newCep: string) => {
    const formatted = newCep.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substr(0, 9);
    setBillingCep(formatted);
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingBillingCep(true);
    try {
      const cepData = await fetchCEPInfo(cleanCep);
      if (cepData) {
        const logradouro = (cepData.logradouro || '').trim();
        const neighborhood = (cepData.neighborhood || '').trim();
        const city = (cepData.city || '').trim();
        const state = (cepData.state || '').trim();

        if (logradouro) setBillingEndereco(logradouro);
        if (neighborhood) setBillingBairro(neighborhood);
        if (city) setBillingCidade(city);
        if (state) setBillingEstado(state);

        const patch: Record<string, any> = { use_event_address: false, address_code: cleanCep };
        if (logradouro) patch.address_location = logradouro;
        if (neighborhood) patch.address_neighborhood = neighborhood;
        if (city) patch.address_city = city;
        if (state) patch.address_state = normalizeRegionCode(state);
        upsertBillingAddressPatch(patch);
      }
    } catch (error) {
      console.error('[InfosFesta] Erro buscando CEP de faturamento:', error);
    } finally {
      setLoadingBillingCep(false);
    }
  };

  // Horário state
  const [horarioInicio, setHorarioInicio] = useState("");
  const [horarioFornecedor, setHorarioFornecedor] = useState("");
  const [partyTimeDuration, setPartyTimeDuration] = useState(""); // e.g. "04:00:00"
  // Duração contratada é um teto ("até X horas")? Apenas comunicação.
  const [durationIsMax, setDurationIsMax] = useState(false);
  const [timeValidationResult, setTimeValidationResult] = useState<{ valid: boolean; message?: string; abertura?: string; fechamento?: string }>({ valid: true });

  // Selected suppliers data
  const [selectedSuppliers, setSelectedSuppliers] = useState<any>({});
  const [salgadosSupplier, setSalgadosSupplier] = useState<any>(null);

  // Supplier timings for individual suppliers
  const [supplierTimings, setSupplierTimings] = useState<{
    [supplierId: string]: string;
  }>({});

  // Step 5 - Product Delivery state
  const [showProductDeliveryStep, setShowProductDeliveryStep] = useState(false);
  const [confirmCurrentAddress, setConfirmCurrentAddress] = useState(true);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [simulationEventDate, setSimulationEventDate] = useState<string>("");
  const [eventGuests, setEventGuests] = useState<string>("");
  const [deliveryAddress, setDeliveryAddress] = useState({
    cep: '',
    endereco: '',
    complemento: '',
    numero: '',
    cidade: '',
    estado: '',
    remetente: ''
  });

  // ── Seus Dados (cliente) ──
  const [nomeCompleto, setNomeCompleto] = useState<string>("");
  const [cpfCnpj, setCpfCnpj] = useState<string>("");
  // Inscrição estadual do cliente PJ (número ou ISENTO): vai na nota fiscal; sem ela a SEFAZ recusa a NF-e de CNPJ contribuinte.
  const [inscricaoEstadual, setInscricaoEstadual] = useState<string>("");
  const [telefoneCliente, setTelefoneCliente] = useState<string>("");
  const [emailCliente, setEmailCliente] = useState<string>("");

  // Validation errors state
  const [errors, setErrors] = useState({
    festaNome: false,
    tipoEvento: false,
    endereco: false,
    cep: false,
    supplierTimings: {} as {
      [supplierId: string]: boolean;
    },
    cidade: false,
    estado: false,
    numero: false,
    horarioInicio: false,
    nomeCompleto: false,
    cpfCnpj: false,
    inscricaoEstadual: false,
    telefoneCliente: false,
    emailCliente: false,
  });

  // State for hour_arrived data from supplier_menu
  const [hourArrivedData, setHourArrivedData] = useState<{ supplierName: string; hourArrived: string }[]>([]);
  const [isFixoMenu, setIsFixoMenu] = useState(false);

  // Fetch hour_arrived from supplier_menu for selected products or fallback to supplier_id
  useEffect(() => {
    const fetchHourArrived = async () => {
      // Strategy 1: Try from selectedSuppliers (cart items)
      const menuIds: string[] = [];
      const categories = ['salgados', 'buffet', 'musica', 'doces'];
      categories.forEach(cat => {
        if (selectedSuppliers?.[cat] && Array.isArray(selectedSuppliers[cat])) {
          selectedSuppliers[cat].forEach((item: any) => {
            const id = item?.menu_id || item?.id;
            if (id) menuIds.push(id);
          });
        }
      });

      if (menuIds.length > 0) {
        const { data } = await (supabase as any)
          .from('supplier_menu')
          .select('id, name, hour_arrived, supplier_id, menu_type')
          .in('id', menuIds)
          .not('hour_arrived', 'is', null);
        
        if (data && data.length > 0) {
          const infos = data.map((item: any) => ({
            supplierName: item.name || 'Fornecedor',
            hourArrived: sanitizeArrivalOffset(item.hour_arrived)
          })).filter((item: any) => item.hourArrived);
          setHourArrivedData(infos);
          setIsFixoMenu(data.some((item: any) => item.menu_type === 'fixo'));
          return;
        }
      }

      const fetchPartyTimeDuration = async () => {
        if (!targetEventId) {
          setPartyTimeDuration('');
          return;
        }

        const { data: childEvents } = await supabase
          .from('events')
          .select('party_time, menu, delivery_supplier, event_hour_is_max')
          .eq('parent_event_id', targetEventId)
          .limit(10);

        const childDuration = childEvents?.map(resolveStoredEventDuration).find(Boolean) || '';
        if (childDuration) {
          setDurationIsMax(Boolean(childEvents?.some((c: any) => c.event_hour_is_max)));
          setPartyTimeDuration(childDuration);
          void saveEventPatch({ party_time: childDuration });
          return;
        }

        const { data: mainEvent } = await supabase
          .from('events')
          .select('party_time, menu, delivery_supplier, event_hour_is_max')
          .eq('id', targetEventId)
          .maybeSingle();

        const resolvedDuration = resolveStoredEventDuration(mainEvent);
        setDurationIsMax(Boolean((mainEvent as any)?.event_hour_is_max));
        setPartyTimeDuration(resolvedDuration);
        if (resolvedDuration) {
          void saveEventPatch({ party_time: resolvedDuration });
        }
      };

      // Strategy 2: Fallback - fetch from event's selected menu items (events.menu)
      if (!targetEventId) {
        setHourArrivedData([]);
        setPartyTimeDuration('');
        return;
      }

      const { data: eventData } = await supabase
        .from('events')
        .select('supplier_id, menu')
        .eq('id', targetEventId)
        .maybeSingle();

      const suppId = eventData?.supplier_id;
      // Extract productIds actually chosen by the client for this event
      const eventMenu: any[] = Array.isArray((eventData as any)?.menu) ? (eventData as any).menu : [];
      const selectedProductIds = eventMenu
        .map((m: any) => m?.productId || m?.product_id || m?.menu_id || m?.id)
        .filter(Boolean);

      if (!suppId && selectedProductIds.length === 0) {
        setHourArrivedData([]);
        await fetchPartyTimeDuration();
        return;
      }

      // Only fetch hour_arrived for the specific menus chosen — not all supplier menus
      let menuQuery = (supabase as any)
        .from('supplier_menu')
        .select('id, name, hour_arrived, menu_type')
        .is('parent_menu_id', null)
        .not('hour_arrived', 'is', null);

      if (selectedProductIds.length > 0) {
        menuQuery = menuQuery.in('id', selectedProductIds);
      } else if (suppId) {
        // No selection recorded — skip to avoid showing unrelated menus (e.g. Crepe alongside Churrasco)
        setHourArrivedData([]);
        await fetchPartyTimeDuration();
        return;
      }

      const { data: menuData } = await menuQuery;

      if (menuData && menuData.length > 0) {
        const infos = menuData.map((item: any) => ({
          supplierName: item.name || 'Fornecedor',
          hourArrived: sanitizeArrivalOffset(item.hour_arrived)
        })).filter((item: any) => item.hourArrived);
        setHourArrivedData(infos);
        setIsFixoMenu(menuData.some((item: any) => item.menu_type === 'fixo'));
      } else {
        setHourArrivedData([]);
      }

      // Also check fixo from all menus (even without hour_arrived)
      if (!menuData?.some((item: any) => item.menu_type === 'fixo')) {
        const { data: allMenus } = await (supabase as any)
          .from('supplier_menu')
          .select('menu_type')
          .eq('supplier_id', suppId)
          .is('parent_menu_id', null)
          .eq('menu_type', 'fixo')
          .limit(1);
        setIsFixoMenu(!!(allMenus && allMenus.length > 0));
      }

      await fetchPartyTimeDuration();
    };
    fetchHourArrived();
  }, [selectedSuppliers, targetEventId]);

  // Login form state
  const [isLoginView, setIsLoginView] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpfField] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // State para controlar qual etapa mostrar
  // Login não é mais necessário antes de infos-festa - inicia direto no formulário
  const [currentStep, setCurrentStep] = useState<'login' | 'form' | 'success'>('form');
  // Carousel step for the form (índice 1-based na lista dinâmica de passos)
  const [carouselStep, setCarouselStep] = useState(1);
  /**
   * Snapshot de quais passos já estão completos, capturado ao carregar um
   * orçamento/evento existente. O passo inicial só é calculado depois que a
   * configuração da jornada (passos ocultos) estiver resolvida.
   */
  const [stepCompletionSnapshot, setStepCompletionSnapshot] = useState<JourneyStepCompletion | null>(null);
  const progression = useFormProgression({
    festaNome,
    tipoEvento,
    endereco,
    cep,
    cidade,
    estado,
    numero,
    selectedSuppliers
  });

  // Lista ativa de passos — mesma fonte usada pelo stepper e pelo auto-advance.
  const journeyStepKeys = useMemo(
    () => buildJourneyStepKeys({
      hideEvent: hideStepEvent,
      hideDatetime: hideStepDatetime,
      hasOnlyDeliveryProducts: progression.hasOnlyDeliveryProducts,
    }),
    [hideStepEvent, hideStepDatetime, progression.hasOnlyDeliveryProducts]
  );

  // Auto-advance aplicado apenas quando já sabemos quais passos existem.
  useEffect(() => {
    if (!stepCompletionSnapshot) return;
    setCarouselStep(resolveInitialJourneyStep(journeyStepKeys, stepCompletionSnapshot));
    setStepCompletionSnapshot(null);
  }, [stepCompletionSnapshot, journeyStepKeys]);

  // Nunca deixar o passo ativo apontar para um índice inexistente.
  useEffect(() => {
    setCarouselStep(prev => Math.max(1, Math.min(prev, journeyStepKeys.length)));
  }, [journeyStepKeys.length]);

  // Rolagem só DEPOIS que o novo passo foi renderizado: calcular antes usa a
  // altura do passo anterior (maior) e o navegador acaba parando no meio.
  const didMountStepScroll = useRef(false);
  useEffect(() => {
    if (!didMountStepScroll.current) {
      didMountStepScroll.current = true;
      return;
    }
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const element = formRef.current;
        if (!element) return;
        const y = element.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [carouselStep]);

  const { calculateAmeUpTax } = useAmeUpTax();
  const { checkoutTax } = usePlatformTax();
  const tiposEvento = ["Aniversário", "Confraternização", "Chá de Panela", "Chá de Cozinha", "Batizado", "Coffee Break", "Outros"];
  const [themeReady, setThemeReady] = useState(!!loadSupplierCustomization()?.supplierSlug);
  const [eventSupplierId, setEventSupplierId] = useState<string | null>(loadSupplierCustomization()?.supplierId || null);
  const [menuType, setMenuType] = useState<string | null>(null);

  // Fetch menuType to determine availability rules
  useEffect(() => {
    if (!eventSupplierId || !targetEventId) return;
    (async () => {
      try {
        const { data: eventData } = await supabase
          .from('events')
          .select('menu')
          .eq('id', targetEventId)
          .maybeSingle();

        const menuName = typeof eventData?.menu === 'string' ? eventData.menu : null;
        if (menuName) {
          const { data: menuData } = await supabase
            .from('supplier_menu')
            .select('menu_type')
            .eq('supplier_id', eventSupplierId)
            .eq('name', menuName)
            .is('parent_menu_id', null)
            .maybeSingle();
          if (menuData) setMenuType(menuData.menu_type);
        }
      } catch (err) {
        console.warn('Error fetching menuType for availability:', err);
      }
    })();
  }, [eventSupplierId, targetEventId]);

  const { isDateDisabled, validateTime, validateDateOrWarn, availabilityData } = useSupplierAvailability(eventSupplierId, menuType === 'selecao' ? 'selecao' : 'evento', { eventType: tipoEvento, guests: eventGuests });

  // Fonte única de verdade para a linguagem de "pedido" (encomendas / cardápio Seleção).
  // `inferredMenuType` vem de `events.menu_types` (mais confiável) ou do parâmetro da URL;
  // `menuType` vem do casamento com `supplier_menu`, que pode falhar em encomendas.
  const isOrderJourney = !eventHasFixo && (inferredMenuType === 'selecao' || menuType === 'selecao');
  // Jornada de evento/serviço (menu_types = fixo ou selecao+fixo): textos em 1ª pessoa,
  // falando de "início do serviço" e "duração" em vez de entrega/festa.
  const isServiceJourney = !isOrderJourney && (eventHasFixo || menuType === 'fixo' || inferredMenuType === 'evento');

  // Efeito dedicado: lê `events.menu_types` independentemente de `skipSteps` na URL.
  useEffect(() => {
    const eventRef = isValidUuid(eventIdParam)
      ? eventIdParam
      : (isValidUuid(simulationId) ? simulationId : null);
    if (!eventRef) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any)
          .from('events')
          .select('menu_types')
          .eq('id', eventRef)
          .maybeSingle();
        if (cancelled) return;
        const types: string[] = Array.isArray(data?.menu_types) ? data.menu_types : [];
        if (types.length > 0) {
          setInferredMenuType(types.includes('selecao') && !types.includes('fixo') ? 'selecao' : 'evento');
        }
      } catch (err) {
        console.warn('Erro ao inferir menu_types do evento:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventIdParam, simulationId]);

  // Delivery area coverage state
  const [supplierRanges, setSupplierRanges] = useState<CepRange[]>([]);
  const [deliveryValidation, setDeliveryValidation] = useState<DeliveryValidationResult | null>(null);
  const [isValidatingDelivery, setIsValidatingDelivery] = useState(false);
  const [deliveryMessage, setDeliveryMessage] = useState<{ type: 'error' | 'warn' | 'success'; text: string } | null>(null);
  const cepValidationRef = useRef<string>('');

  // Load supplier coverage areas
  useEffect(() => {
    if (!eventSupplierId) return;
    (async () => {
      const data = await fetchCoverageRowsForContext(
        eventSupplierId,
        isOrderJourney ? 'order' : 'event',
      );
      setSupplierRanges((data || []).map(rowToCepRange));
    })();
  }, [eventSupplierId, isOrderJourney]);

  // Replica o novo CEP e o frete no pedido (total_value), nos itens do pedido e no carrinho da sessão.
  const syncAddressFreightEverywhere = useCallback(async (cleanCep: string, fee: number) => {
    if (!targetEventId) return;
    try {
      const { data: ev } = await supabase
        .from('events')
        .select('calculated_total, discount_value, discount_code, supplier_id')
        .eq('id', targetEventId)
        .maybeSingle();
      const itemsSubtotal = Number(ev?.calculated_total || 0);
      // Cupom de frete grátis: o desconto acompanha o novo frete.
      const shippingCoupon = (ev as any)?.discount_code ? await fetchShippingCoupon((ev as any).discount_code, (ev as any).supplier_id) : null;
      const freeShipping = !!shippingCoupon;
      const discount = shippingCoupon ? calculateCouponDiscount(shippingCoupon, { subtotal: itemsSubtotal, freight: fee }) : Number(ev?.discount_value || 0);
      const patch: Record<string, any> = { cep: cleanCep, delivery_fee: fee, ...(freeShipping ? { discount_value: discount } : {}) };
      if (itemsSubtotal > 0) {
        const total = Math.max(0, Math.round((itemsSubtotal + fee - discount) * 100) / 100);
        patch.total_value = total;
        patch.event_value_total = String(total);
      }
      await saveEventPatch(patch);
      await supabase
        .from('events')
        .update({ cep: cleanCep })
        .eq('parent_event_id', targetEventId)
        .eq('is_active', true);
    } catch (err) {
      console.warn('[InfosFesta] Não foi possível replicar CEP/frete no pedido:', err);
    }

    try {
      const raw = sessionStorage.getItem('supplier_cart_items');
      if (raw) {
        const items = JSON.parse(raw);
        if (Array.isArray(items) && items.length > 0) {
          sessionStorage.setItem('supplier_cart_items', JSON.stringify(items.map((item: any) => ({ ...item, cep: cleanCep }))));
        }
      }
      const slug = loadSupplierCustomization()?.supplierSlug || sessionStorage.getItem('ameup_last_supplier_slug') || '';
      if (slug) {
        const key = `store_filters_${slug}`;
        const saved = JSON.parse(sessionStorage.getItem(key) || '{}');
        sessionStorage.setItem(key, JSON.stringify({ ...saved, cep: cleanCep }));
      }
      window.dispatchEvent(new Event('cart-filters-updated'));
    } catch { /* sem sessionStorage */ }
  }, [targetEventId, saveEventPatch]);

  // Validate CEP coverage whenever cep, ranges, or guests change
  const runDeliveryValidation = useCallback(async (cleanCep: string) => {
    // Em retirada no local não há área de entrega a validar
    if (isPickup || cleanCep.length !== 8 || supplierRanges.length === 0) {
      setDeliveryValidation(null);
      setDeliveryMessage(null);
      return;
    }
    cepValidationRef.current = cleanCep;
    setIsValidatingDelivery(true);
    try {
      const guestCount = parseInt(eventGuests || '1', 10) || 1;
      const result = await checkDeliveryAreaCoverage(cleanCep, supplierRanges, guestCount);
      if (cepValidationRef.current !== cleanCep) return;
      setDeliveryValidation(result);

      if (result.isUnverified) {
        setDeliveryMessage({
          type: 'warn',
          text: 'Não conseguimos confirmar automaticamente sua região. O frete será confirmado pelo fornecedor.',
        });
      } else if (!result.isValid && !result.area) {
        setDeliveryMessage({ type: 'error', text: '😔 Ainda não atendemos esta região' });
      } else if (result.isDisabled) {
        setDeliveryMessage({ type: 'error', text: 'Entregas suspensas nesta região no momento.' });
      } else {

        // Mesma conta do carrinho e do checkout: frete da região + pedágio estimado. Antes só aparecia o frete da
        // região e o pedágio entrava depois, então o aviso mostrava um valor e o pedido outro.
        let tollAmount = 0;
        if (proposalDeliveryFee == null && (result.deliveryFee ?? 0) > 0 && result.coordinates && eventSupplierId) {
          const quote = await fetchTollQuote(
            eventSupplierId,
            { lat: result.coordinates.lat, lng: result.coordinates.lng },
            isOrderJourney ? 'order' : 'event',
          );
          if (cepValidationRef.current !== cleanCep) return;
          tollAmount = quote.amount;
        }
        const fee = proposalDeliveryFee ?? (Math.round(((result.deliveryFee ?? 0) + tollAmount) * 100) / 100);

        // Grava o novo CEP/frete no pedido e no carrinho já ao digitar: antes só era salvo ao avançar, e
        // proposta, carrinho e checkout seguiam com o endereço e o frete antigos.
        if (proposalDeliveryFee == null && targetEventId) {
          void syncAddressFreightEverywhere(cleanCep, fee);
        }
        if (fee > 0) {
          setDeliveryMessage({
            type: 'warn',
            text: `Localização atualizada - Frete: R$ ${fee.toFixed(2).replace('.', ',')}`,
          });
        } else {
          setDeliveryMessage({
            type: 'success',
            text: 'Localização atualizada - sem custo adicional',
          });
        }
      }
    } catch (err) {
      console.error('[InfosFesta] Erro ao validar área de entrega:', err);
    } finally {
      if (cepValidationRef.current === cleanCep) setIsValidatingDelivery(false);
    }
  }, [supplierRanges, eventGuests, isPickup, proposalDeliveryFee, eventSupplierId, isOrderJourney, targetEventId, syncAddressFreightEverywhere]);

  // Re-run validation when ranges load or guests change (if CEP is valid)
  useEffect(() => {
    const cleanCep = (cep || '').replace(/\D/g, '');
    if (cleanCep.length === 8) {
      runDeliveryValidation(cleanCep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierRanges, eventGuests]);

  // ── Retirada no local: pré-preencher o endereço do fornecedor ──
  const [pickupSupplierAddress, setPickupSupplierAddress] = useState<{
    full_address: string | null;
    address_name?: string | null;
    address_number?: string | null;
    address_complement?: string | null;
    city?: string | null;
    state?: string | null;
    address_code?: string | null;
  } | null>(null);
  const pickupPrefilledRef = useRef(false);

  useEffect(() => {
    const supplierId = eventSupplierId || supplierIdState;
    if (!isPickup || !supplierId || pickupSupplierAddress) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('public-pickup-address', {
          body: { supplier_id: supplierId },
        });
        if (cancelled || error || !data) return;
        setPickupSupplierAddress(data as any);
      } catch (err) {
        console.warn('[InfosFesta] Falha ao carregar endereço de retirada:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPickup, eventSupplierId, supplierIdState, pickupSupplierAddress]);

  // No modo retirada o endereço é sempre o do fornecedor (não editável pelo cliente)
  useEffect(() => {
    if (!isPickup || !pickupSupplierAddress || pickupPrefilledRef.current) return;
    pickupPrefilledRef.current = true;


    const patch: Record<string, any> = {};
    if (pickupSupplierAddress.address_code) {
      const clean = String(pickupSupplierAddress.address_code).replace(/\D/g, '');
      setCep(clean.replace(/^(\d{5})(\d{3})$/, '$1-$2'));
      patch.address_code = clean;
    }
    if (pickupSupplierAddress.address_name) {
      setEndereco(pickupSupplierAddress.address_name);
      patch.address_location = pickupSupplierAddress.address_name;
    }
    if (pickupSupplierAddress.address_number) {
      setNumero(String(pickupSupplierAddress.address_number));
      patch.address_number = String(pickupSupplierAddress.address_number);
    }
    if (pickupSupplierAddress.address_complement) {
      setComplemento(pickupSupplierAddress.address_complement);
      patch.adress_complement = pickupSupplierAddress.address_complement;
    }
    if (pickupSupplierAddress.city) {
      setCidade(pickupSupplierAddress.city);
      patch.address_city = pickupSupplierAddress.city;
    }
    if (pickupSupplierAddress.state) {
      setEstado(pickupSupplierAddress.state);
      patch.address_state = normalizeRegionCode(pickupSupplierAddress.state);
    }
    if (Object.keys(patch).length > 0) {
      upsertEventLocationPatch(patch);
      if (patch.address_code) saveEventPatch({ cep: patch.address_code });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickup, pickupSupplierAddress]);

  // Auto-advance removed — user must click "Próximo" button to proceed from address step



  useEffect(() => {
    if (!horarioInicio || !simulationEventDate) return;
    const dateObj = parseDateFromStorage(simulationEventDate);
    if (!dateObj) return;
    const durationForValidation = partyTimeDuration ? partyTimeDuration.substring(0, 5) : undefined;
    const result = validateTime(dateObj, horarioInicio, durationForValidation);
    setTimeValidationResult(result);
  }, [horarioInicio, simulationEventDate, partyTimeDuration, validateTime]);

  // Check if Step 3 (location) is complete
  const locationComplete = endereco.trim() && cep.trim() && bairro.trim() && cidade.trim() && estado.trim() && numero.trim() && simulationEventDate;

  // Load supplier customization from simulation if cache is empty
  useEffect(() => {
    const loadSupplierThemeFromSimulation = async () => {
      const cached = loadSupplierCustomization();
      if (cached?.supplierSlug) {
        setThemeReady(true);
        return; // Already cached
      }

      const targetId = simulationId || eventIdParam;
      if (!targetId) return;

      try {
        // Get supplier_id from the event/simulation (try parent first, then child)
        let supplierId: string | null = null;

        const { data: eventData } = await supabase
          .from('events')
          .select('supplier_id')
          .eq('id', targetId)
          .maybeSingle();

        supplierId = eventData?.supplier_id || null;

        // If parent doesn't have supplier_id, try child events
        if (!supplierId) {
          const { data: childEvent } = await supabase
            .from('events')
            .select('supplier_id')
            .eq('parent_event_id', targetId)
            .not('supplier_id', 'is', null)
            .limit(1)
            .maybeSingle();
          
          supplierId = childEvent?.supplier_id || null;
        }

        if (!supplierId) return;

        // Load supplier data
        const { data: supplier } = await (supabase as any)
          .from('suppliers_public')
          .select('id, name, slug, user_id, whatsapp_number')
          .eq('id', supplierId)
          .maybeSingle();

        if (!supplier?.slug) return;

        // Load logo from profile avatar_url
        let logoUrl: string | undefined;
        if (supplier.user_id) {
          const { data: profile } = await supabase
            .from('profile')
            .select('avatar_url')
            .eq('id', supplier.user_id)
            .maybeSingle();
          logoUrl = profile?.avatar_url || undefined;
        }

        // Load store customization
        const { data: customization } = await (supabase as any)
          .from('supplier_store_customization')
          .select('accent_color, button_color, text_color, bottom_header_color, banner_url, show_logo')
          .eq('supplier_id', supplier.id)
          .maybeSingle();

        // Use banner_url as logo fallback if no avatar
        const finalLogo = logoUrl || customization?.banner_url || undefined;

        setEventSupplierId(supplier.id);
        saveSupplierCustomization({
          supplierId: supplier.id,
          supplierSlug: supplier.slug,
          supplierName: supplier.name || '',
          whatsappNumber: (supplier as any).whatsapp_number || undefined,
          logoUrl: customization?.show_logo ? finalLogo : undefined,
          accentColor: customization?.accent_color || '',
          buttonColor: customization?.button_color || '',
          buttonTextColor: customization?.text_color || '',
          headerColor: customization?.bottom_header_color || customization?.button_color || '',
          iconColor: customization?.accent_color || '',
          source: 'digital_menu',
        });
        setThemeReady(true);
      } catch (error) {
        console.error('Error loading supplier theme for InfosFesta:', error);
      }
    };

    loadSupplierThemeFromSimulation();
  }, [simulationId, eventIdParam]);

  // ── Rascunho local do formulário ──
  // Ao voltar do checkout (ou da loja) o cliente não pode perder o que já digitou: espelhamos os campos na
  // sessão da aba e, depois que o carregamento do pedido termina, preenchemos só o que ainda está vazio
  // (o que vem do banco continua valendo).
  // Chave estável por loja (não por simulação): voltar à loja pode gerar outro pedido e o rascunho precisa continuar.
  const legacyDraftKey = `ameup_infos_draft:${simulationId || eventIdParam || (() => { try { return sessionStorage.getItem('ameup_parent_event_id') || ''; } catch { return ''; } })() || 'x'}`;
  const draftKey = `ameup_infos_draft:store:${(() => { try { return sessionStorage.getItem('ameup_last_supplier_slug') || 'default'; } catch { return 'default'; } })()}`;
  const isProposalMode = (() => { try { return new URLSearchParams(window.location.search).get('mode') === 'orcamento'; } catch { return false; } })();
  const draftFields = {
    tipoEvento, horarioInicio, partyTimeDuration, festaNome,
    cep, endereco, numero, complemento, bairro, cidade, estado,
    nomeCompleto, cpfCnpj, telefoneCliente, emailCliente,
  };
  const draftSignature = JSON.stringify(draftFields);
  useEffect(() => {
    if (loadingSimulation || isProposalMode) return;
    try {
      if (Object.values(draftFields).some((v) => String(v ?? '').trim())) {
        sessionStorage.setItem(draftKey, draftSignature);
      }
    } catch { /* sessionStorage indisponível */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSignature, draftKey, loadingSimulation]);
  useEffect(() => {
    if (loadingSimulation) return;
    try {
      if (isProposalMode) return;
      const raw = sessionStorage.getItem(draftKey) || sessionStorage.getItem(legacyDraftKey);
      if (!raw) return;
      const d = JSON.parse(raw) as Partial<typeof draftFields>;
      const fill = (setter: React.Dispatch<React.SetStateAction<string>>, value?: string) => {
        if (value && String(value).trim()) setter((prev) => (prev && String(prev).trim() ? prev : value));
      };
      fill(setTipoEvento, d.tipoEvento);
      fill(setHorarioInicio, d.horarioInicio);
      fill(setPartyTimeDuration, d.partyTimeDuration);
      fill(setFestaNome, d.festaNome);
      fill(setCep, d.cep);
      fill(setEndereco, d.endereco);
      fill(setNumero, d.numero);
      fill(setComplemento, d.complemento);
      fill(setBairro, d.bairro);
      fill(setCidade, d.cidade);
      fill(setEstado, d.estado);
      fill(setNomeCompleto, d.nomeCompleto);
      fill(setCpfCnpj, d.cpfCnpj);
      fill(setTelefoneCliente, d.telefoneCliente);
      fill(setEmailCliente, d.emailCliente);
    } catch { /* rascunho ilegível: ignora */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSimulation, draftKey]);

  // Load simulation data on mount
  // Respeita requireLogin para mostrar login primeiro se necessário
  useEffect(() => {
    // Login é solicitado apenas no momento do pagamento (PaymentAuthModal).
    // Aqui o cliente sempre vai direto para o formulário, mesmo anônimo.
    setCurrentStep('form');
    if (eventIdParam && user) {
      loadEventById(eventIdParam);
    } else if (simulationId) {
      if (user) {
        linkAndLoadSimulation();
      } else {
        loadSimulationData();
      }
    } else if (user) {
      loadLatestUserEventFallback();
    }
  }, [simulationId, user, eventIdParam]);

  // Handle OAuth return and continue simulation flow
  useEffect(() => {
    if (user && !loading && !loadingSimulation) {
      const loginOrigin = localStorage.getItem('loginOrigin');
      const pendingSimulationId = localStorage.getItem('pendingSimulationId');
      const returnToForm = localStorage.getItem('returnToForm');
      const passwordResetOrigin = localStorage.getItem('passwordResetOrigin');
      
      logInfo('User authentication completed', {
        component: 'InfosFesta',
        action: 'user_auth',
        metadata: {
          hasLoginOrigin: !!loginOrigin,
          hasPendingSimulation: !!pendingSimulationId,
          hasCurrentSimulation: !!simulationId,
          shouldReturnToForm: !!returnToForm
        }
      });
      
      // Handle return from OAuth without simulationId in URL
      if (!simulationId && (loginOrigin === 'simulation' || loginOrigin === 'infos-festa' || passwordResetOrigin === 'simulation') && pendingSimulationId) {
        // Clear the stored data
        localStorage.removeItem('pendingSimulationId');
        localStorage.removeItem('loginOrigin');
        localStorage.removeItem('returnToForm');
        localStorage.removeItem('passwordResetOrigin');
        
        logInfo('OAuth redirect initiated', { component: 'InfosFesta', action: 'oauth_redirect' });
        navigate(`/infos-festa?simulationId=${pendingSimulationId}`, { replace: true });
        return;
      }
      
      // Handle OAuth return when already on infos-festa page or login from infos-festa
      if ((loginOrigin === 'simulation' || loginOrigin === 'infos-festa') && simulationId) {
        logInfo('OAuth flow completed', { component: 'InfosFesta', action: 'continue_simulation' });
        localStorage.removeItem('loginOrigin');
        localStorage.removeItem('returnToForm');
        setCurrentStep('form');
      }
      
      // Handle direct email/password login on infos-festa (returnToForm flag)
      if (returnToForm === 'true' && (loginOrigin === 'infos-festa' || loginOrigin === 'simulation')) {
        logInfo('Login completed, returning to form', { component: 'InfosFesta', action: 'return_to_form' });
        localStorage.removeItem('loginOrigin');
        localStorage.removeItem('returnToForm');
        setCurrentStep('form');
      }
    }
  }, [user, simulationId, navigate, loading, loadingSimulation]);

  // Fallback: Detect OAuth return from home page
  useEffect(() => {
    const checkOAuthReturn = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthParams = urlParams.has('access_token') || urlParams.has('code');
      const referrerFromHome = document.referrer.includes('ameup.com.br/#') || 
                              document.referrer.includes('ameup.com.br#') ||
                              window.location.hash === '#';
      
      logDebug('OAuth return check completed', {
        component: 'InfosFesta',
        metadata: {
          hasAuthParams,
          referrerFromHome,
          hasSimulationId: !!simulationId
        }
      });
      
      // If user arrived from home page after OAuth and we're on infos-festa with simulationId
      if (user && (hasAuthParams || referrerFromHome) && simulationId && window.location.pathname.includes('infos-festa')) {
        logInfo('OAuth home page return detected', { component: 'InfosFesta', action: 'continue_simulation' });
        setCurrentStep('form');
        // Clean URL parameters
        if (hasAuthParams) {
          const cleanParams = new URLSearchParams();
          cleanParams.set('simulationId', simulationId);
          window.history.replaceState({}, '', `/infos-festa?${cleanParams.toString()}`);
        }
      }
    };

    if (user && !loading) {
      // Small delay to ensure all auth processing is complete
      setTimeout(checkOAuthReturn, 500);
    }
  }, [user, loading, simulationId]);

  const linkAndLoadSimulation = async (isFormStep = true) => {
    if (!simulationId || !user) {
      logDebug('Simulation linking skipped - missing data', {
        component: 'InfosFesta',
        metadata: {
          hasSimulationId: !!simulationId,
          hasUser: !!user
        }
      });
      return;
    }
    
    logInfo('Simulation linking process started', {
      component: 'InfosFesta',
      action: 'link_simulation'
    });
    
    try {
      // Verificar se a sessão está ativa no Supabase
      const { data: sessionCheck } = await supabase.auth.getSession();
      logDebug('Session verification completed', {
        component: 'InfosFesta',
        metadata: { hasActiveSession: !!sessionCheck.session }
      });

      // Tentar vincular a simulação anônima ao usuário
      logDebug('Attempting simulation linking', { component: 'InfosFesta' });
      const linked = await linkAnonymousSimulationByIdToUser(simulationId, user.id);
      if (linked) {
        logInfo('Simulation linked successfully', { component: 'InfosFesta', action: 'link_success' });
      } else {
        logError('Simulation linking failed', undefined, { component: 'InfosFesta', action: 'link_failed' });
      }

      // Only update status to "Detalhes da Festa" when user is actually on the form step (not login)
      // The status should remain in previous state while the user is still authenticating
      if (isFormStep) {
        try {
          await supabase.rpc('advance_shared_quote_status', {
            p_event_id: simulationId,
            p_new_status: 'Detalhes da Festa'
          });
          logInfo('Event status updated to Detalhes da Festa via RPC', { component: 'InfosFesta' });
        } catch (statusErr) {
          console.error('Error updating status via RPC:', statusErr);
          // Fallback to direct update if RPC fails
          const now = new Date().toISOString();
          await (supabase as any).from('events').update({ status: 'Detalhes da Festa', geral_status: 'Detalhes da Festa', updated_at: now }).eq('id', simulationId);
          await (supabase as any).from('events').update({ status: 'Detalhes da Festa', geral_status: 'Detalhes da Festa', updated_at: now }).eq('parent_event_id', simulationId);
        }
      }

      // Tentar recuperar evento já criado a partir da simulação
      const restored = await tryResolveEventFromSimulation();
      if (restored) {
        logInfo('Event restored from simulation', { component: 'InfosFesta', action: 'event_restored' });
        return;
      }

      // Carregar os dados da simulação
      logDebug('Loading simulation data', { component: 'InfosFesta' });
      await loadSimulationData();
    } catch (error) {
      logError('Simulation linking process failed', error as Error, { component: 'InfosFesta' });
      // Mesmo com erro na vinculação, tentar carregar os dados
      const restored = await tryResolveEventFromSimulation();
      if (!restored) {
        await loadSimulationData();
      }
    }
  };

  const loadSimulationData = async () => {
    const idToLoad = targetEventId;
    if (!idToLoad) return;
    setLoadingSimulation(true);
    try {
      const SIMULATION_COLUMNS = 'menu, event_date, session_info, cep, guests, event_code, event_hour, event_type, party_time, discount_value, discount_code, total_value, calculated_total, delivery_fee, is_pickup';

      let {
        data: simulation,
        error
      } = await (supabase as any).from('events').select(SIMULATION_COLUMNS).eq('id', idToLoad).maybeSingle();

      // A proposta pode pertencer a outra sessão anônima (link aberto em outro
      // aparelho/aba). Nesse caso a RLS devolve vazio: pedimos a transferência
      // de posse e tentamos ler novamente uma única vez.
      if (!simulation && !error) {
        const claim = await claimSharedQuote({ eventId: idToLoad });
        if (claim.success) {
          const retry = await (supabase as any).from('events').select(SIMULATION_COLUMNS).eq('id', idToLoad).maybeSingle();
          simulation = retry.data;
          error = retry.error;
        } else if (claim.error === 'OWNER_IS_REGISTERED_USER') {
          toast.error(claim.message || 'Esta proposta pertence a outra conta. Faça login para continuar.');
        }
      }

      if (error) {
        console.error('Error loading simulation:', error);
        toast.error('Erro ao carregar dados da simulação');
        return;
      }

      // Nenhum registro visível (RLS/sessão anônima diferente): avisa o cliente
      // em vez de seguir com o formulário vazio e falhar apenas no envio.
      if (!simulation) {
        console.warn('Simulation not visible for current session:', idToLoad);
        toast.error('Não conseguimos carregar sua proposta. Volte ao cardápio e tente novamente.');
        return;
      }

      if (simulation) {
        // Set event code
        if (simulation.event_code) {
          setEventCode(simulation.event_code);
        }

        setIsPickup(pickupRequested || !!simulation.is_pickup);

        // Store event date for delivery calculations
        if (simulation.event_date) {
          setSimulationEventDate(simulation.event_date);
        }

        // Store guests count
        if (simulation.guests) {
          setEventGuests(simulation.guests);
        }

        // Store event type and hour
        if (isRealEventType(simulation.event_type)) {
          const stored = splitStoredEventType(simulation.event_type);
          setTipoEvento(stored.tipo);
          if (stored.outros) updateTipoEventoOutros(stored.outros);
          setEventTypeFromDb(true);
        }

        if (simulation.event_hour) {
          setHorarioInicio(simulation.event_hour);
        }
        const resolvedDuration = resolveStoredEventDuration(simulation);
        if (resolvedDuration) {
          setPartyTimeDuration(resolvedDuration);
          if (!sanitizeDurationValue(simulation.party_time)) {
            void saveEventPatch({ party_time: resolvedDuration });
          }
        }

        // Load saved address from event_location FIRST (before CEP fetch to avoid overwriting)
        let locationLoaded = false;
        try {
          const { data: locData } = await (supabase as any)
            .from('event_location')
            .select('address_location, address_number, adress_complement, address_code, Adress_neighborhood, address_city, address_state')
            .eq('event_id', idToLoad)
            .maybeSingle();
          if (locData) {

            if (locData.address_location) { setEndereco(locData.address_location); locationLoaded = true; }
            if (locData.address_number) { setNumero(locData.address_number); locationLoaded = true; }
            if (locData.adress_complement) setComplemento(locData.adress_complement);
            if (locData.Adress_neighborhood) setBairro(locData.Adress_neighborhood);
            if (locData.address_city) { setCidade(locData.address_city); locationLoaded = true; }
            if (locData.address_state) { setEstado(locData.address_state); locationLoaded = true; }
            if (locData.address_code) {
              const displayCep = String(locData.address_code).replace(/^(\d{5})(\d{3})$/, '$1-$2');
              setCep(displayCep);
            }
          }
        } catch (e) {
          console.error('Error loading event_location:', e);
        }

        // Only auto-fetch from ViaCEP if event_location didn't have address data
        if (simulation.cep) {
          if (!locationLoaded) {
            setCep(simulation.cep);
            await handleCepAutoFetch(simulation.cep);
          } else if (!cep) {
            // Set CEP display if not already set from event_location
            const displayCep = String(simulation.cep).replace(/^(\d{5})(\d{3})$/, '$1-$2');
            setCep(displayCep);
          }
        }

        // Load selected suppliers data from child events menu field
        // (the parent's 'menu' field may have supplier data in legacy format)
        if (simulation.menu && typeof simulation.menu === 'object' && simulation.menu !== null) {
          const normalizedSuppliers = normalizeSelectedSuppliers(simulation.menu);
          setSelectedSuppliers(normalizedSuppliers);
          if (normalizedSuppliers.salgados) {
            setSalgadosSupplier(normalizedSuppliers.salgados);
          } else {
            setSalgadosSupplier(null);
          }
          if (hasDeliveryProducts(normalizedSuppliers)) {
            setShowProductDeliveryStep(true);
          }
        } else {
          setSalgadosSupplier(null);
        }

        // Try to load client info from sessionStorage first for immediate display
        try {
          const cachedClientInfo = sessionStorage.getItem(`checkout_client_info_${idToLoad}`);
          if (cachedClientInfo) {
            const parsed = JSON.parse(cachedClientInfo);
            if (parsed.fullName) setNomeCompleto(parsed.fullName);
            if (parsed.email) setEmailCliente(parsed.email);
            if (parsed.phone) setTelefoneCliente(parsed.phone);
            if (parsed.cpf) setCpfCnpj(parsed.cpf);
          }
        } catch (e) {
          console.warn('Error loading cached client info:', e);
        }

        // Fetch client info from the database (event_client_info table)
        try {
          const { data: clientInfo, error: clientErr } = await (supabase as any)
            .from('event_client_info')
            .select('client_name, client_email, client_phone, client_cpf, client_state_registration')
            .eq('event_id', idToLoad)
            .maybeSingle();

          if (clientInfo && !clientErr) {

            if (clientInfo.client_name) setNomeCompleto(clientInfo.client_name);
            if (clientInfo.client_email) setEmailCliente(clientInfo.client_email);
            if (clientInfo.client_phone) setTelefoneCliente(clientInfo.client_phone);
            if (clientInfo.client_cpf) setCpfCnpj(clientInfo.client_cpf);
            if (clientInfo.client_state_registration) setInscricaoEstadual(clientInfo.client_state_registration);
          }
        } catch (e) {
          console.error('Error loading client info from DB:', e);
        }
      }

      // Auto-advance carouselStep based on already-filled data
      // Step 1 = event type, Step 2 = address, Step 3 = date/time
      const hasEventType = !!simulation.event_type;
      const hasCep = !!simulation.cep;
      const hasDate = !!simulation.event_date;
      const hasTime = !!simulation.event_hour;

      // Also check if event_location has address data
      const { data: locCheck } = await (supabase as any)
        .from('event_location')
        .select('address_location, address_number, address_city, address_state')
        .eq('event_id', idToLoad)
        .maybeSingle();
      const hasAddress = !!(locCheck?.address_location && locCheck?.address_number && locCheck?.address_city && locCheck?.address_state);

      // Check client info from DB data just fetched
      const { data: clientInfoCheck } = await (supabase as any)
        .from('event_client_info')
        .select('client_name, client_email, client_phone, client_cpf, client_state_registration')
        .eq('event_id', idToLoad)
        .maybeSingle();
      const hasClientInfo = !!(clientInfoCheck?.client_name && clientInfoCheck?.client_email && clientInfoCheck?.client_phone && clientInfoCheck?.client_cpf && (String(clientInfoCheck.client_cpf).replace(/\D/g, '').length !== 14 || parseStateRegistration(clientInfoCheck.client_state_registration)));

      // O passo inicial é resolvido em efeito, quando a lista de passos ativos
      // (Jornada do Cliente) já estiver conhecida.
      setStepCompletionSnapshot({
        event: hasEventType,
        address: hasAddress || hasCep,
        time: hasDate && hasTime,
        data: hasClientInfo,
      });


    } catch (error) {
      console.error('Error loading simulation:', error);
      toast.error('Erro ao carregar dados da simulação');
    } finally {
      setLoadingSimulation(false);
    }
  };

  const loadEventById = async (eventId: string) => {
    setLoadingSimulation(true);
    try {
      // Query only columns that exist in the events table
      const { data: event, error } = await supabase
        .from('events')
        .select('event_type, event_date, guests, cep, party_time, menu, event_code, event_hour, discount_value, discount_code, total_value, calculated_total, delivery_fee, is_pickup, created_by_supplier_id')
        .eq('id', eventId)
        .maybeSingle();
      if (error) {
        console.error('Error loading event:', error);
        return false;
      }
      if (!event) return false;

      if (event.event_code) setEventCode(event.event_code);
      setProposalDeliveryFee((event as any).created_by_supplier_id && event.delivery_fee != null ? Number(event.delivery_fee) || 0 : null);
      setIsPickup(pickupRequested || !!(event as any).is_pickup);
      const storedEventType = isRealEventType(event.event_type) ? splitStoredEventType(event.event_type as string) : null;
      setTipoEvento(storedEventType ? storedEventType.tipo : '');
      if (storedEventType?.outros) updateTipoEventoOutros(storedEventType.outros);
      if (isRealEventType(event.event_type)) setEventTypeFromDb(true);

      setHorarioInicio(event.event_hour || '');
      const resolvedDuration = resolveStoredEventDuration(event);
      if (resolvedDuration) {
        setPartyTimeDuration(resolvedDuration);
        if (!sanitizeDurationValue(event.party_time)) {
          void saveEventPatch({ party_time: resolvedDuration });
        }
      }
      if (event.event_date) setSimulationEventDate(String(event.event_date));
      if (event.guests) setEventGuests(String(event.guests));

      // Load address from event_location table
      let hasAddr = false;
      try {
        const { data: locData } = await (supabase as any)
          .from('event_location')
          .select('address_location, address_number, adress_complement, address_code, Adress_neighborhood, address_city, address_state')
          .eq('event_id', eventId)
          .maybeSingle();
        if (locData) {
          if (locData.address_location) setEndereco(locData.address_location);
          if (locData.address_number) setNumero(locData.address_number);
          if (locData.adress_complement) setComplemento(locData.adress_complement);
          if (locData.Adress_neighborhood) setBairro(locData.Adress_neighborhood);
          if (locData.address_city) setCidade(locData.address_city);
          if (locData.address_state) setEstado(locData.address_state);
          if (locData.address_code) {
            const displayCep = String(locData.address_code).replace(/^(\d{5})(\d{3})$/, '$1-$2');
            setCep(displayCep);
          }
          hasAddr = !!(locData.address_location && locData.address_number && locData.address_city && locData.address_state);
        }
      } catch (e) {
        console.error('Error loading event_location for event:', e);
      }

      // Sync pickup state from event
      if (event.is_pickup !== undefined) {
        setIsPickup(!!event.is_pickup);
      }

      // Fallback: if no address from event_location but event has cep, auto-fetch
      if (!hasAddr && event.cep && !event.is_pickup) {
        const displayCep = String(event.cep).replace(/^(\d{5})(\d{3})$/, '$1-$2');
        setCep(displayCep);
        await handleCepAutoFetch(event.cep);
      }

      // Load suppliers from menu field
      if (event.menu && typeof event.menu === 'object') {
        const normalized = normalizeSelectedSuppliers(event.menu as any);
        setSelectedSuppliers(normalized);
        if (normalized.salgados) setSalgadosSupplier(normalized.salgados);
        if (hasDeliveryProducts(normalized)) {
          setShowProductDeliveryStep(true);
        }
      }

      // Try to load client info from sessionStorage first
      try {
        const cachedClientInfo = sessionStorage.getItem(`checkout_client_info_${eventId}`);
        if (cachedClientInfo) {
          const parsed = JSON.parse(cachedClientInfo);
          if (parsed.fullName) setNomeCompleto(parsed.fullName);
          if (parsed.email) setEmailCliente(parsed.email);
          if (parsed.phone) setTelefoneCliente(parsed.phone);
          if (parsed.cpf) setCpfCnpj(parsed.cpf);
        }
      } catch (err) {
        console.warn('Error loading cached client info for event:', err);
      }

      // Fetch client info from the database (event_client_info table)
      try {
        const { data: clientInfo, error: clientErr } = await (supabase as any)
          .from('event_client_info')
          .select('client_name, client_email, client_phone, client_cpf, client_state_registration')
          .eq('event_id', eventId)
          .maybeSingle();

        if (clientInfo && !clientErr) {

          if (clientInfo.client_name) setNomeCompleto(clientInfo.client_name);
          if (clientInfo.client_email) setEmailCliente(clientInfo.client_email);
          if (clientInfo.client_phone) setTelefoneCliente(clientInfo.client_phone);
          if (clientInfo.client_cpf) setCpfCnpj(clientInfo.client_cpf);
          if (clientInfo.client_state_registration) setInscricaoEstadual(clientInfo.client_state_registration);
        }
      } catch (err) {
        console.error('Error loading client info from DB for event:', err);
      }

      // Auto-advance carouselStep based on filled data
      const hasType = !!event.event_type;
      const hasTimeSet = !!event.event_hour;
      const hasDateSet = !!event.event_date;
      
      // Check client info from DB data just fetched
      const { data: clientInfoCheck } = await (supabase as any)
        .from('event_client_info')
        .select('client_name, client_email, client_phone, client_cpf, client_state_registration')
        .eq('event_id', eventId)
        .maybeSingle();
      const hasClientInfo = !!(clientInfoCheck?.client_name && clientInfoCheck?.client_email && clientInfoCheck?.client_phone && clientInfoCheck?.client_cpf && (String(clientInfoCheck.client_cpf).replace(/\D/g, '').length !== 14 || parseStateRegistration(clientInfoCheck.client_state_registration)));

      // O passo inicial é resolvido em efeito (ver resolveInitialJourneyStep).
      setStepCompletionSnapshot({
        event: hasType,
        address: hasAddr,
        time: hasDateSet && hasTimeSet,
        data: hasClientInfo,
      });


      
      return true;
    } catch (e) {
      console.error('Error loading event by id:', e);
      return false;
    } finally {
      setLoadingSimulation(false);
    }
  };

  // Fallback function to load latest active event for user
  const loadLatestUserEventFallback = async () => {
    if (!user) return;
    
    setLoadingSimulation(true);

    
    try {
      const { data: latestEvent, error } = await supabase
        .from('events')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['programado', 'aguardando pagamento', 'em preparacao'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading latest event:', error);
        toast.error('Erro ao carregar dados de eventos');
        return;
      }

      if (latestEvent) {

        
        // Update URL to include the found eventId
        const newParams = new URLSearchParams(searchParams);
        newParams.set('eventId', latestEvent.id);
        navigate(`/infos-festa?${newParams.toString()}`, { replace: true });
        
        // Load the event data
        await loadEventById(latestEvent.id);
        
      } else {

      }
    } catch (error) {
      console.error('Error in fallback event loading:', error);
      toast.error('Erro ao carregar dados de eventos');
    } finally {
      setLoadingSimulation(false);
    }
  };

  const tryResolveEventFromSimulation = async (): Promise<boolean> => {
    if (!simulationId) return false;
    try {
      const { data: sim, error } = await (supabase as any)
        .from('events')
        .select('session_info')
        .eq('id', simulationId)
        .maybeSingle();
      if (error) {
        console.error('Error resolving event from simulation:', error);
        return false;
      }
      const createdEventId = (sim?.session_info as any)?.created_event_id;
      if (createdEventId) {
        return await loadEventById(createdEventId);
      }
    } catch (e) {
      console.error('Error in tryResolveEventFromSimulation:', e);
    }
    return false;
  };

  const handleCepAutoFetch = async (cepValue: string) => {
    const cleanCep = cepValue.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const cepData = await fetchCEPInfo(cleanCep);
      console.log('[InfosFesta] ViaCEP retornou para', cleanCep, cepData);
      if (cepData) {
        const logradouro = (cepData.logradouro || '').trim();
        const neighborhood = (cepData.neighborhood || '').trim();
        const city = (cepData.city || '').trim();
        const state = (cepData.state || '').trim();

        // Sobrescreve sempre que o ViaCEP devolver um valor (não-vazio).
        // Para CEPs genéricos (sem logradouro/bairro), preserva o valor atual em vez de apagar.
        if (logradouro) {
          setEndereco(logradouro);
          if (errors.endereco) setErrors((prev: any) => ({ ...prev, endereco: false }));
        }
        if (neighborhood) setBairro(neighborhood);
        if (city) {
          setCidade(city);
          if (errors.cidade) setErrors((prev: any) => ({ ...prev, cidade: false }));
        }
        if (state) {
          setEstado(state);
          if (errors.estado) setErrors((prev: any) => ({ ...prev, estado: false }));
        }

        // Persiste apenas os campos efetivamente retornados + o CEP
        const patch: Record<string, any> = { address_code: cleanCep };
        if (logradouro) patch.address_location = logradouro;
        if (neighborhood) patch.Adress_neighborhood = neighborhood;
        if (city) patch.address_city = city;
        if (state) patch.address_state = normalizeRegionCode(state);
        upsertEventLocationPatch(patch);
        saveEventPatch({ cep: cleanCep });
      } else {
        console.warn('[InfosFesta] ViaCEP não retornou dados para', cleanCep);
      }
    } catch (error) {
      console.error('[InfosFesta] Erro buscando CEP:', error);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = async (newCep: string) => {
    const formatted = newCep.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substr(0, 9);
    setCep(formatted);
    if (errors.cep && formatted.trim()) {
      setErrors(prev => ({
        ...prev,
        cep: false
      }));
    }

    // Autosave CEP to both events.cep and event_location.address_code
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      debouncedSaveEventPatch({ cep: cleanCep });
      debouncedUpsertLocationPatch({ address_code: cleanCep });
      // Validate delivery area coverage for the supplier
      runDeliveryValidation(cleanCep);
    } else {
      setDeliveryValidation(null);
      setDeliveryMessage(null);
    }

    // Auto-fetch address when CEP is complete
    await handleCepAutoFetch(formatted);
  };

  // Helpers de máscara/validação para "Seus Dados"
  const formatCpfCnpj = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };
  const isValidCPF = (cpf: string): boolean => {
    const d = cpf.replace(/\D/g, '');
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
    let rev = 11 - (sum % 11);
    if (rev >= 10) rev = 0;
    if (rev !== parseInt(d[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
    rev = 11 - (sum % 11);
    if (rev >= 10) rev = 0;
    return rev === parseInt(d[10]);
  };
  const isValidCNPJ = (cnpj: string): boolean => {
    const d = cnpj.replace(/\D/g, '');
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
    const calc = (base: string, weights: number[]): number => {
      const sum = weights.reduce((acc, w, i) => acc + parseInt(base[i]) * w, 0);
      const r = sum % 11;
      return r < 2 ? 0 : 11 - r;
    };
    const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    return calc(d.slice(0, 12), w1) === parseInt(d[12]) && calc(d.slice(0, 13), w2) === parseInt(d[13]);
  };
  const isValidCpfCnpj = (value: string): boolean => {
    const d = value.replace(/\D/g, '');
    if (d.length === 11) return isValidCPF(d);
    if (d.length === 14) return isValidCNPJ(d);
    return false;
  };
  const cpfCnpjErrorMessage = (value: string): string => {
    const d = value.replace(/\D/g, '');
    if (!d) return 'Informe seu CPF ou CNPJ.';
    if (d.length < 11) return 'CPF incompleto. Informe os 11 dígitos.';
    if (d.length > 11 && d.length < 14) return 'CNPJ incompleto. Informe os 14 dígitos.';
    if (d.length === 11 && !isValidCPF(d)) return 'CPF inválido. Verifique os dígitos.';
    if (d.length === 14 && !isValidCNPJ(d)) return 'CNPJ inválido. Verifique os dígitos.';
    return 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.';
  };
  const formatTelefone = (value: string): string => {
    const d = value.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10) {
      return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    }
    return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
  };
  const isValidTelefone = (value: string): boolean => value.replace(/\D/g, '').length === 11;
  const isValidEmail = (value: string): boolean => isValidEmailStrict(value);

  // Quando o passo "Tipo de evento" é ocultado pela Jornada do Cliente,
  // preenchemos um valor padrão para não travar o checkout na validação.
  // O horário NÃO recebe mais um padrão forçado aqui: quando o passo "Data e
  // Horário" é ocultado (ex.: pedidos/encomendas sem horário de entrega
  // relevante), horarioInicio fica vazio e event_hour é persistido como
  // undefined/null em vez de um horário falso tipo "12:00" — a validação
  // já trata horarioInicio como opcional (ver `horarioInicio: false` em
  // validateForm).
  useEffect(() => {
    if (hideStepEvent && !tipoEvento) {
      let stored: string | null = null;
      try {
        stored = sessionStorage.getItem('ameup_event_type');
        if (!stored) {
          const raw = sessionStorage.getItem('supplier_cart_items');
          const items = raw ? JSON.parse(raw) : [];
          stored = Array.isArray(items) ? (items.find((i: any) => !!i?.eventType)?.eventType || null) : null;
        }
      } catch { stored = null; }
      setTipoEvento(stored || 'Evento');
    }
  }, [hideStepEvent, tipoEvento]);

  const validateForm = () => {

    const newErrors = {
      festaNome: false, // Auto-generated, no longer required from user
      // Tipo de evento só é obrigatório se o Passo 1 estiver visível
      tipoEvento: !hideStepEvent && (!tipoEvento || outrosPendente),

      endereco: !endereco.trim(),
      cep: !cep.trim(),
      cidade: !cidade.trim(),
      estado: !estado.trim(),
      numero: false,
      horarioInicio: false,
      nomeCompleto: !nomeCompleto.trim(),
      cpfCnpj: !isValidCpfCnpj(cpfCnpj),
      // CNPJ exige a inscrição estadual (ou isento / não contribuinte): sem ela a SEFAZ recusa a nota fiscal.
      inscricaoEstadual: cpfCnpj.replace(/\D/g, '').length === 14 && !parseStateRegistration(inscricaoEstadual),
      telefoneCliente: !isValidTelefone(telefoneCliente),
      emailCliente: !isValidEmail(emailCliente) && !user?.email,
      supplierTimings: {} as {
        [supplierId: string]: boolean;
      }
    };

    // Se não marcou "sem número" e não preencheu número, é erro (apenas se não for retirada)
    if (!numero.trim()) {
      newErrors.numero = true;
    }

    // Only validate supplier timings if we're not dealing with delivery-only products
    if (!progression.hasOnlyDeliveryProducts && !hideStepDatetime) {

      // Validate supplier timings - check if there are relevant suppliers and if they have timing set
      const relevantSuppliers = getRelevantSuppliers();
      relevantSuppliers.forEach(supplier => {
        if (!supplierTimings[supplier.id]?.trim()) {
          newErrors.supplierTimings[supplier.id] = true;
        }
      });
    }

    setErrors(newErrors);

    // Verificar se há algum erro
    const hasBasicErrors = Object.entries(newErrors).some(([key, error]) => {
      if (key === 'supplierTimings') return false; // Check supplier timing errors separately
      return error;
    });
    const hasSupplierTimingErrors = Object.values(newErrors.supplierTimings).some(error => error);
    
    // Don't check supplier timing errors for delivery-only products
    const shouldCheckSupplierTimings = !progression.hasOnlyDeliveryProducts && !hideStepDatetime;
    
    if (hasBasicErrors || (shouldCheckSupplierTimings && hasSupplierTimingErrors)) {
      toast.error("Por favor, preencha todos os campos obrigatórios marcados em vermelho");
      return false;
    }
    return true;
  };

  // Helper function to get relevant suppliers (synchronized with ImprovedTimeSection)
  const getRelevantSuppliers = () => {
    const suppliers: any[] = [];

    // Process salgados suppliers - extract supplier_id and fetch from database
    if (selectedSuppliers?.salgados && Array.isArray(selectedSuppliers.salgados)) {
      selectedSuppliers.salgados.forEach((supplier: any) => {
        // Usar supplier_id (novo) ou id (compatibilidade)
        const supplierId = supplier?.supplier_id || supplier?.id;
        if (supplierId) {
          suppliers.push({
            id: supplierId,
            name: supplier?.supplier?.name || supplier?.name || 'Fornecedor de Salgados',
            logo_url: supplier?.supplier?.logo_url || supplier?.logo_url,
            items: supplier?.items || []
          });
        }
      });
    }

    // Process doces suppliers - group by supplier_id
    if (selectedSuppliers?.doces && Array.isArray(selectedSuppliers.doces)) {
      const docesBySupplier: {
        [supplierId: string]: any;
      } = {};
      selectedSuppliers.doces.forEach((item: any) => {
        const supplierId = item?.supplier_id || item?.id;
        if (supplierId) {
          if (!docesBySupplier[supplierId]) {
            docesBySupplier[supplierId] = {
              id: supplierId,
              name: item?.supplier?.name || item?.name || 'Fornecedor de Doces',
              logo_url: item?.supplier?.logo_url || item?.logo_url,
              items: [],
              productTypes: new Set()
            };
          }
          docesBySupplier[supplierId].items.push(item);

          // Detect product types for unified display
          if (item?.name?.toLowerCase().includes('bolo') || item?.category === 'bolo') {
            docesBySupplier[supplierId].productTypes.add('Bolo');
          } else {
            docesBySupplier[supplierId].productTypes.add('Doces');
          }
        }
      });

      // Add grouped doces suppliers
      Object.values(docesBySupplier).forEach((supplier: any) => {
        suppliers.push({
          ...supplier,
          productTypes: Array.from(supplier.productTypes)
        });
      });
    }
    return suppliers;
  };

  // Function to get the display address for delivery
  const getDeliveryDisplayAddress = () => {
    // If we have a delivery address and it's not confirmed as current address, show the alternative
    if (deliveryAddress.cep && !confirmCurrentAddress) {
      return {
        formatted1: `${deliveryAddress.endereco}, ${deliveryAddress.numero}${deliveryAddress.complemento ? ` - ${deliveryAddress.complemento}` : ''}`,
        formatted2: `${deliveryAddress.cidade} - ${deliveryAddress.estado} | CEP: ${deliveryAddress.cep}`,
        recipient: deliveryAddress.remetente,
        isAlternative: true
      };
    }
    // Otherwise, show the event address
    return {
      formatted1: `${endereco}, ${numero}${complemento ? ` - ${complemento}` : ''}`,
      formatted2: `${cidade} - ${estado} | CEP: ${cep}`,
      recipient: null,
      isAlternative: false
    };
  };

  // Function to check if there are decoration or disposable products
  const hasDeliveryProducts = (suppliers: any = selectedSuppliers) => {
    const hasDecoracao = suppliers?.decoracao && Array.isArray(suppliers.decoracao) && suppliers.decoracao.length > 0;
    const hasDescartaveis = suppliers?.descartaveis && Array.isArray(suppliers.descartaveis) && suppliers.descartaveis.length > 0;
    return hasDecoracao || hasDescartaveis;
  };

  // Function to calculate delivery date (event date minus 2 business days)
  const calculateDeliveryDate = (eventDate: string) => {
    if (!eventDate) return '';
    try {
      const eventDateObj = parseISO(eventDate);
      const deliveryDate = addBusinessDays(eventDateObj, -2);
      return format(deliveryDate, 'dd/MM/yyyy', {
        locale: ptBR
      });
    } catch (error) {
      console.error('Error calculating delivery date:', error);
      return '';
    }
  };

  // Function to get delivery suppliers and item count
  const getDeliverySuppliers = () => {
    const suppliers: any[] = [];
    let totalItems = 0;

    // Process decoration suppliers
    if (selectedSuppliers?.decoracao && Array.isArray(selectedSuppliers.decoracao)) {
      const decoracaoBySupplier: {
        [supplierId: string]: any;
      } = {};
      selectedSuppliers.decoracao.forEach((item: any) => {
        const supplierId = item?.supplier_id || item?.suppliers?.id;
        totalItems += item?.quantity || 1;
        if (supplierId && !decoracaoBySupplier[supplierId]) {
          decoracaoBySupplier[supplierId] = {
            id: supplierId,
            name: item?.suppliers?.name || 'Fornecedor de Decoração',
            logo_url: item?.suppliers?.logo_url || null
          };
        }
      });
      suppliers.push(...Object.values(decoracaoBySupplier));
    }

    // Process disposable suppliers
    if (selectedSuppliers?.descartaveis && Array.isArray(selectedSuppliers.descartaveis)) {
      const descartaveisBySupplier: {
        [supplierId: string]: any;
      } = {};
      selectedSuppliers.descartaveis.forEach((item: any) => {
        const supplierId = item?.supplier_id || item?.suppliers?.id;
        totalItems += item?.quantity || 1;
        if (supplierId && !descartaveisBySupplier[supplierId]) {
          descartaveisBySupplier[supplierId] = {
            id: supplierId,
            name: item?.suppliers?.name || 'Fornecedor de Descartáveis',
            logo_url: item?.suppliers?.logo_url || null
          };
        }
      });
      suppliers.push(...Object.values(descartaveisBySupplier));
    }
    return {
      suppliers,
      totalItems
    };
  };

  // Handle delivery address change
  const handleDeliveryAddressChange = async (newCep: string) => {
    const formatted = newCep.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substr(0, 9);
    setDeliveryAddress(prev => ({
      ...prev,
      cep: formatted
    }));

    // Auto-fetch address when CEP is complete
    if (formatted.replace(/\D/g, '').length === 8) {
      setLoadingDeliveryCep(true);
      try {
        const cepData = await fetchCEPInfo(formatted.replace(/\D/g, ''));
        if (cepData) {
          setDeliveryAddress(prev => ({
            ...prev,
            endereco: cepData.logradouro || '',
            cidade: cepData.city || '',
            estado: cepData.state || ''
          }));
          
        } else {
          toast.error('CEP não encontrado. Verifique o número digitado.');
        }
      } catch (error) {
        console.error('Error fetching CEP for delivery address:', error);
        toast.error('Erro ao buscar CEP. Tente novamente.');
      } finally {
        setLoadingDeliveryCep(false);
      }
    }
  };

  // Validation helpers
  const validateCPF = (cpfVal: string): boolean => {
    const digits = cpfVal.replace(/\D/g, '');
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
    let remainder = (sum * 10) % 11;
    if (remainder === 10) remainder = 0;
    if (remainder !== parseInt(digits[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
    remainder = (sum * 10) % 11;
    if (remainder === 10) remainder = 0;
    return remainder === parseInt(digits[10]);
  };

  const validatePhone = (phoneVal: string): boolean => {
    const digits = phoneVal.replace(/\D/g, '');
    return digits.length === 11;
  };

  const formatPhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const formatCPF = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const validateSignupFields = (): boolean => {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.fullName = 'Nome completo é obrigatório';
    if (!isValidEmailStrict(email)) errs.email = 'E-mail inválido';
    if (!validatePhone(phone)) errs.phone = 'Celular inválido (DDD + 9 dígitos)';
    if (!validateCPF(cpf)) errs.cpf = 'CPF inválido';
    if (password.length < 6) errs.password = 'Senha deve ter no mínimo 6 caracteres';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateLoginFields = (): boolean => {
    const errs: Record<string, string> = {};
    if (!isValidEmailStrict(email)) errs.email = 'E-mail inválido';
    if (!password.trim()) errs.password = 'Senha é obrigatória';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoginView) {
      if (!validateLoginFields()) return;
    } else {
      if (!validateSignupFields()) return;
    }

    setLoading(true);
    try {
      if (isLoginView) {
        localStorage.setItem('loginOrigin', 'infos-festa');
        localStorage.setItem('loginOriginAt', String(Date.now()));
        localStorage.setItem('returnToForm', 'true');
        if (simulationId) {
          localStorage.setItem('pendingSimulationId', simulationId);
        }
        
        const { error } = await signIn(email, password, 'infos-festa');
        if (error) {
          toast.error("Erro ao fazer login", {
            description: error.message || "Verifique suas credenciais e tente novamente."
          });
        } else {
          
          setCurrentStep('form');
          if (simulationId) {
            linkAndLoadSimulation();
          }
        }
      } else {
        localStorage.setItem('loginOrigin', 'infos-festa');
        localStorage.setItem('loginOriginAt', String(Date.now()));
        localStorage.setItem('returnToForm', 'true');
        if (simulationId) {
          localStorage.setItem('pendingSimulationId', simulationId);
        }
        
        const { error, data: signUpData } = await signUp(email, password, fullName, phone.replace(/\D/g, ''));
        if (error) {
          toast.error("Erro ao criar conta", {
            description: error.message || "Verifique suas informações e tente novamente."
          });
        } else {
          // Update profile with CPF, phone and role_id
          const userId = signUpData?.user?.id;
          if (userId) {
            const cpfDigits = cpf.replace(/\D/g, '');
            await supabase.from('profile').update({
              document_number: cpfDigits,
              phone: phone.replace(/\D/g, ''),
              role_id: '4e042985-4c19-4bd1-87b4-416e8bdefb90',
            }).eq('id', userId);
          }
          setCurrentStep('form');
          if (simulationId) {
            linkAndLoadSimulation();
          }
        }
      }
    } catch (error: any) {
      toast.error("Ocorreu um erro", {
        description: error.message || "Tente novamente mais tarde."
      });
    }
    setLoading(false);
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google':
        return 'Google';
      case 'facebook':
        return 'Facebook';
      default:
        return provider;
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setLoading(true);
    try {
      // Store context for OAuth return to infos-festa form
      localStorage.setItem('loginOrigin', 'infos-festa');
      localStorage.setItem('loginOriginAt', String(Date.now()));
      localStorage.setItem('returnToForm', 'true');
      // Save full return URL so AuthContext can redirect back after OAuth
      const returnUrl = window.location.pathname + window.location.search;
      localStorage.setItem('postLoginRedirect', returnUrl);
      if (simulationId) {
        localStorage.setItem('pendingSimulationId', simulationId);
      }
      
      // Social login will redirect to OAuth, no need to handle success here
      const { error } = await signInWithProvider(provider);
      if (error) {
        toast.error(`Erro ao fazer login com ${getProviderDisplayName(provider)}`, {
          description: error.message || "Tente novamente mais tarde."
        });
        setLoading(false);
      }
      // Don't set loading to false here as the page will redirect
    } catch (error: any) {
      toast.error("Erro no login social", {
        description: error.message || "Tente novamente mais tarde."
      });
      setLoading(false);
    }
  };

  const generateEventCode = async () => {
    // Gerar código único usando timestamp + random ID (thread-safe)
    const randomId = Math.random().toString(36).substr(2, 9).toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `EVT-${timestamp}-${randomId}`;
  };

  /**
   * Calcula o VALOR MONETÁRIO da taxa da plataforma (checkout_tax)
   * baseado no total do evento
   */
  const calculateCheckoutTaxValue = (
    eventTotalValue: number,
    checkoutTaxRate: number
  ): number => {
    // Se checkout_tax for 0 ou null, retornar 0
    if (!checkoutTaxRate || checkoutTaxRate === 0) {
      return 0;
    }
    
    // Reverter cálculo: Total / (1 + taxa/100) = Subtotal
    const subtotal = eventTotalValue / (1 + checkoutTaxRate / 100);
    
    // Calcular valor monetário da taxa: Subtotal × taxa%
    const taxValue = subtotal * (checkoutTaxRate / 100);
    
    
    return formatMonetaryValue(taxValue);
  };

  // Função para gerar dados de entrega dos fornecedores
  const generateDeliveryData = async (
    selectedSuppliers: any, 
    eventCode: string, 
    eventDate: string, 
    supplierTimings: any = {}, 
    deliveryAddressData?: any, 
    useCurrentAddress: boolean = true, 
    guestCount?: number,
    eventTotalValue?: number
  ) => {

    // 1. Normalizar dados usando convertToOptimizedStructure
    const optimizedData = convertToOptimizedStructure(selectedSuppliers, guestCount);
    const deliveryData = [];
    // Indica se algum cardápio contratado usa duração máxima ("até X horas")
    let eventHourIsMax = false;

    // ✅ Calcular valor monetário da taxa da plataforma (se checkout_tax > 0)
    const checkoutTaxValue = eventTotalValue 
      ? calculateCheckoutTaxValue(eventTotalValue, checkoutTax)
      : 0;


    // 2. Processar dados já agrupados por supplier_id (chaves do objeto)
    for (const [supplierId, supplierInfo] of Object.entries(optimizedData)) {
      try {

        // Buscar dados do fornecedor na tabela suppliers (incluindo method_supplier)
        const {
          data: supplierData,
          error: supplierError
        } = await (supabase as any).from('suppliers_public').select('id, name, service_type').eq('id', supplierId).maybeSingle();
        if (supplierError || !supplierData) {
          console.warn('Supplier not found:', supplierId, supplierError);
          continue;
        }

        // Buscar account do fornecedor via view restrita
        let supplierAccountId = null;
        try {
          const { data: supplierAccountData, error: accountError } = await (supabase as any)
            .from('suppliers_payment_accounts')
            .select('account')
            .eq('id', supplierId)
            .maybeSingle();

          if ((supplierAccountData as any)?.account && !accountError) {
            supplierAccountId = (supplierAccountData as any).account;
          } else {
            console.warn(`Supplier ${supplierData.name} has no account field - using null`);
          }
        } catch (error) {
          console.error(`Error fetching account for supplier ${supplierData.name}:`, error);
          supplierAccountId = null;
        }

        // Agrupar itens por categoria
        const itemsByCategory: {
          [key: string]: any[];
        } = {};
        (supplierInfo as any).items.forEach((item: any) => {
          const category = item.category || 'outros';
          if (!itemsByCategory[category]) {
            itemsByCategory[category] = [];
          }
          itemsByCategory[category].push(item);
        });

        // Calcular subtotal dos itens deste fornecedor
        const itemsSubtotal = formatMonetaryValue((supplierInfo as any).items.reduce((total: number, item: any) => {
          const itemPrice = formatMonetaryValue(item.totalPrice || item.price || 0);
          return total + itemPrice;
        }, 0));

        // Verificar se este fornecedor tem decoração E descartáveis para determinar taxa
        const hasDecoracao = itemsByCategory['decoracao'];
        const hasDescartaveis = itemsByCategory['descartaveis'];
        const shouldUnifyDecoracaoDescartaveis = hasDecoracao && hasDescartaveis;

        // ✅ Verificar se é categoria de decoração (mantido apenas para referência)
        const isDecorationCategory = shouldUnifyDecoracaoDescartaveis || hasDecoracao || hasDescartaveis;
        
        // ✅ REMOVIDO: Taxa não é mais adicionada ao totalPrice - será item separado
        const totalPrice = itemsSubtotal; // Sempre subtotal puro, sem taxa embutida


        // ✅ SEMPRE calcular ameup_tax (comissão do fornecedor) - aceita retorno 0
        const ameupTaxResult = await calculateAmeUpTax(1, 'others', [supplierId]);
        const ameupTaxPercentage = ameupTaxResult.success && ameupTaxResult.taxRate ? ameupTaxResult.taxRate : 0;
        

        // Calcular ameup_value sobre totalPrice (que já inclui checkout_tax para decoração)
        const ameupValue = formatMonetaryValue((totalPrice * ameupTaxPercentage) / 100);
        const totalSplit = formatMonetaryValue(totalPrice - ameupValue);


        // Gerar estrutura de pagamento único para todos os fornecedores
        const methodSupplier = supplierData.method_supplier || 100;
        const eventDateObj = parseDateFromStorage(eventDate);
        
        // Calcular date_custody usando Edge Function
        let dateCustody = new Date(eventDateObj);
        dateCustody.setDate(eventDateObj.getDate() + 14); // Fallback default
        
        try {
          const { data, error } = await supabase.functions.invoke('calculate-payment-date', {
            body: { baseDate: eventDate }
          });
          
          if (!error && data?.paymentDate) {
            dateCustody = new Date(data.paymentDate);
          }
        } catch (error) {
          console.error('Error calculating date_custody:', error);
          // Keeps fallback value already set above
        }
        
        // Criar único objeto de pagamento conforme nova especificação
        const payments = [{
          account_id: supplierAccountId,
          date: formatDateForStorage(new Date()), // Data atual local
          status: 'aguardando pagamento',  
          percentage: methodSupplier,
          ameup_tax: ameupTaxPercentage,
          ameup_value: formatMonetaryValue(ameupValue),
          total_price: formatMonetaryValue(totalPrice),
          total_split: formatMonetaryValue(totalSplit),
          date_custody: formatDateForStorage(dateCustody)
        }];

        // 3. Verificar se este fornecedor tem decoração E descartáveis para unificar (já verificado acima)
        // const hasDecoracao = itemsByCategory['decoracao'];
        // const hasDescartaveis = itemsByCategory['descartaveis'];
        // const shouldUnifyDecoracaoDescartaveis = hasDecoracao && hasDescartaveis;

        // As variáveis de cálculo já foram definidas acima
        // ameupTaxPercentage, ameupValue, totalSplit já estão disponíveis

        // Se deve unificar decoração e descartáveis, criar um registro unificado
        if (shouldUnifyDecoracaoDescartaveis) {
          
          // Combinar itens de decoração e descartáveis
          const combinedItems = [...hasDecoracao, ...hasDescartaveis];
          
          // Preparar lista de itens do menu unificada
          const unifiedMenuItems = combinedItems.map((item: any) => {
            const itemName = item.name || item.product_name || item.product || item.sub_product || `${item.product || 'Produto'} ${item.sub_product || ''}`.trim();
            return {
              name: itemName,
              category: item.category || 'outros',
              quantity: item.quantity || 1,
              ...(item.event_hour_time && {
                event_hour_time: item.event_hour_time
              }),
              ...(item.color && {
                color: item.color
              }),
              ...(item.size && {
                size: item.size
              }),
              ...(item.material && {
                material: item.material
              })
            };
          });

          // ✅ REMOVIDO: checkout_tax não é mais item nos menu_items
          // Taxa será tratada como item separado no delivery_supplier com category: "tax"

          // Determinar endereço de entrega para categoria unificada
          const shouldUseAlternativeAddress = !useCurrentAddress && deliveryAddressData;
          
          // Criar registro unificado para decoração + descartáveis
          const unifiedDeliveryRecord = {
            order_number: eventCode,
            supplier_id: (supplierData as any).id,
            supplier_name: (supplierData as any).name,
            supplier_logo: (supplierData as any).logo_url,
            category: 'decoracao_descartaveis', // Nova categoria unificada
            date: calculateDeliveryDate(eventDate), // 2 dias úteis antes do evento
            menu_items: unifiedMenuItems,
            value: formatMonetaryValue(itemsSubtotal), // ✅ Subtotal SEM taxa
            guest_count: guestCount,
            delivery_address: shouldUseAlternativeAddress ? {
              address_code: formatCepForStorage(deliveryAddressData.cep),
              address_location: deliveryAddressData.endereco,
              address_number: deliveryAddressData.numero,
              address_complement: deliveryAddressData.complemento,
              address_city: deliveryAddressData.cidade,
              address_state: normalizeRegionCode(deliveryAddressData.estado),
              recipient_name: deliveryAddressData.remetente
            } : {
              address_code: formatCepForStorage(cep),
              address_location: endereco,
              address_number: numero,
              address_complement: complemento,
              address_city: cidade,
              address_state: normalizeRegionCode(estado)
            },
            start_time: '09:00',
            end_time: '18:00',
            status: 'aguardando pagamento',
            payments: payments,
            created_at: formatDateForStorage(new Date())
          };
          
          deliveryData.push(unifiedDeliveryRecord);
          
          // Remover decoração e descartáveis do processamento individual
          delete itemsByCategory['decoracao'];
          delete itemsByCategory['descartaveis'];
        }

        // 4. Criar um registro separado para cada categoria restante do fornecedor
        for (const [category, categoryItems] of Object.entries(itemsByCategory)) {
          let eventHourTime = null; // Declarar aqui para usar em todo o escopo
          
          // Determinar data baseada na categoria
          let categoryDate: string;
          if (['decoracao', 'descartaveis'].includes(category)) {
            // Decoração e descartáveis: 2 dias úteis antes do evento
            categoryDate = calculateDeliveryDate(eventDate);
          } else {
            // Doces e salgados: data do evento
            categoryDate = eventDate; // Já está no formato YYYY-MM-DD correto
          }

          // Determinar horários baseado na categoria específica
          let startTime = '09:00';
          let endTime = '18:00';

          // Se é categoria com horário específico (salgados, buffet ou doces), usar horário específico do fornecedor
          if (['salgados', 'buffet', 'doces'].includes(category)) {
            // Buscar horário específico para este fornecedor primeiro
            const supplierSpecificTime = supplierTimings[supplierId];
            startTime = supplierSpecificTime || horarioInicio || '09:00';

            // Buscar event_hour_time do produto para calcular endTime
            eventHourTime = null;
            if (['salgados', 'buffet', 'doces'].includes(category) && categoryItems.length > 0) {
              const productId = categoryItems[0].id;
              
              const {
                data: productData
              } = await (supabase as any).from('supplier_menu').select('event_hour_time, event_hour_is_max').eq('id', productId).maybeSingle();
              
              
              if (productData && productData.event_hour_time !== null && productData.event_hour_time !== undefined) {
                eventHourTime = productData.event_hour_time;
                // Flag "até X horas": apenas comunicação, não altera o cálculo de término.
                if (productData.event_hour_is_max) eventHourIsMax = true;
                
                if (category === 'salgados') {
                  endTime = calculateEndTimeWithDayOverflow(startTime, productData.event_hour_time);
                } else if (category === 'doces') {
                  // Para doces, manter o horário do cliente ou configurar como necessário
                  endTime = startTime; // Pode ser ajustado conforme necessário
                }
              }
            }
          } else if (['decoracao', 'descartaveis'].includes(category)) {
            // Decoração e descartáveis sempre usam horário fixo
            startTime = '09:00';
            endTime = '18:00';
          }


          // Calcular subtotal da categoria
          const categorySubtotal = formatMonetaryValue(
            categoryItems.reduce((total: number, item: any) => {
              const itemPrice = formatMonetaryValue(item.totalPrice || item.price || 0);
              return total + itemPrice;
            }, 0)
          );

          // ✅ Verificar se é categoria de decoração (mantido apenas para referência)
          const isCategoryDecoration = ['decoracao', 'descartaveis'].includes(category);

          // ✅ REMOVIDO: Taxa não é mais adicionada ao categoryTotalPrice - será item separado
          const categoryTotalPrice = categorySubtotal; // Sempre subtotal puro, sem taxa embutida


          // ✅ Calcular ameup_tax e payments para categoria individual
          const categoryAmeupTaxResult = await calculateAmeUpTax(1, 'others', [supplierId]);
          const categoryAmeupTaxPercentage = categoryAmeupTaxResult.success && categoryAmeupTaxResult.taxRate 
            ? categoryAmeupTaxResult.taxRate 
            : 0;

          const categoryAmeupValue = formatMonetaryValue((categoryTotalPrice * categoryAmeupTaxPercentage) / 100);
          const categoryTotalSplit = formatMonetaryValue(categoryTotalPrice - categoryAmeupValue);

          // Criar payments para esta categoria
          const categoryPayments = [{
            account_id: supplierAccountId,
            date: formatDateForStorage(new Date()),
            status: 'aguardando pagamento',
            percentage: methodSupplier,
            ameup_tax: categoryAmeupTaxPercentage,
            ameup_value: formatMonetaryValue(categoryAmeupValue),
            total_price: formatMonetaryValue(categoryTotalPrice),
            total_split: formatMonetaryValue(categoryTotalSplit),
            date_custody: formatDateForStorage(dateCustody)
          }];

          // Preparar lista de itens do menu apenas para esta categoria
            const menuItems = categoryItems.map((item: any) => {
            // Para diferentes tipos de produtos, usar campos apropriados para o nome
            const itemName = item.name || item.product_name || item.product || item.sub_product || `${item.product || 'Produto'} ${item.sub_product || ''}`.trim();
            
            
            // Debug da estrutura do item para entender melhor
            
            // Detectar se é um item de bolo - melhorando a detecção
            const isCakeItem = item.type === 'bolo' || 
                             category === 'bolo' || 
                             (category === 'doces' && item.type === 'bolo') ||
                             itemName.toLowerCase().includes('bolo') ||
                             (item.flavor && item.filling); // Se tem sabor e recheio, provavelmente é bolo
            
            const menuItem = {
              name: itemName,
              category: item.category || 'outros',
              quantity: item.quantity || 1,
               ...(eventHourTime !== null && eventHourTime !== undefined && {
                event_hour_time: eventHourTime
              }),
              // Campos específicos para bolos
              ...(isCakeItem && {
                ...(item.size && { size: item.size }),
                ...(item.colors && { colors: item.colors }),
                ...(item.flavor && { flavor: item.flavor }),
                ...(item.details && { details: item.details }),
                ...(item.filling && { filling: item.filling }),
                ...(item.filling2 && { filling2: item.filling2 }),
                ...(item.decoration && { decoration: item.decoration })
              }),
              // Campos para outros produtos (decoração, descartáveis)
              ...(!isCakeItem && {
                ...(item.color && { color: item.color }),
                ...(item.size && { size: item.size }),
                ...(item.material && { material: item.material })
              })
            };
            
            return menuItem;
          });

          // ✅ REMOVIDO: checkout_tax não é mais item nos menu_items
          // Taxa será tratada como item separado no delivery_supplier com category: "tax"


          // Determinar endereço de entrega baseado na categoria e na escolha do usuário
          const isDeliveryCategory = ['decoracao', 'descartaveis'].includes(category);
          const shouldUseAlternativeAddress = isDeliveryCategory && !useCurrentAddress && deliveryAddressData;

          // Criar registro de entrega específico para esta categoria
          const deliveryRecord = {
            order_number: eventCode,
            supplier_id: (supplierData as any).id,
            supplier_name: (supplierData as any).name,
            supplier_logo: (supplierData as any).logo_url,
            category: category,
            date: categoryDate, // Data calculada baseada na categoria
            menu_items: menuItems,
            value: formatMonetaryValue(categorySubtotal), // ✅ Subtotal SEM taxa
            guest_count: guestCount,
            ...(eventHourTime && {
              event_hour_time: eventHourTime
            }),
            delivery_address: isPickup ? {
              address_code: null,
              address_location: "Retirada no local do fornecedor",
              address_number: null,
              address_complement: null,
              address_city: (supplierData as any).city || null,
              address_state: normalizeRegionCode((supplierData as any).state),
              is_pickup: true
            } : (shouldUseAlternativeAddress ? {
              address_code: formatCepForStorage(deliveryAddressData.cep),
              address_location: deliveryAddressData.endereco,
              address_number: deliveryAddressData.numero,
              address_complement: deliveryAddressData.complemento,
              address_city: deliveryAddressData.cidade,
              address_state: normalizeRegionCode(deliveryAddressData.estado),
              recipient_name: deliveryAddressData.remetente
            } : {
              address_code: formatCepForStorage(cep),
              address_location: endereco,
              address_number: numero,
              address_complement: complemento,
              address_city: cidade,
              address_state: normalizeRegionCode(estado)
            }),
            start_time: startTime,
            end_time: endTime,
            status: 'aguardando pagamento',
            payments: categoryPayments, // ✅ Usar payments da categoria
            created_at: formatDateForStorage(new Date())
          };
          deliveryData.push(deliveryRecord);
        }
      } catch (error) {
        console.error('Error processing supplier:', supplierId, error);
        continue;
      }
    }

    // ✅ NOVO: Criar item separado para taxa da plataforma
    if (checkoutTaxValue > 0) {
      
      // Buscar account_id da AmeUp dinamicamente
      const AMEUP_SUPPLIER_ID = "55149252-099b-42ea-a5a5-b3a60750ddc2";
      const { data: ameupSupplier, error: ameupError } = await (supabase as any)
        .from('suppliers_payment_accounts')
        .select('account')
        .eq('id', AMEUP_SUPPLIER_ID)
        .single();

      if (ameupError) {
        console.error('Error fetching AmeUp account:', ameupError);
      }

      const ameupAccountId = ameupSupplier?.account || "ACCO_9EA396FD-832B-4B82-A661-816550D59AD7";

      const platformTaxRecord = {
        category: "tax",
        payments: [{
          tax_name: "platform",
          date: formatDateForStorage(new Date()),
          ameup_tax: 0,
          account_id: ameupAccountId,
          percentage: checkoutTax, // 5.53%
          ameup_value: 0,
          total_price: formatMonetaryValue(checkoutTaxValue),
          total_split: formatMonetaryValue(checkoutTaxValue) // 100% vai para AmeUp
        }]
      };
      
      deliveryData.push(platformTaxRecord);
    }

    // ✅ Add freight record from supplier_cep_coverage (centralized helper)
    let outOfArea = false;
    try {
      if (isPickup) {
        console.log('🚚 [Freight] Skipping freight calculation for pickup order');
      } else {
        const eventCep = formatCepForStorage(cep).replace(/\D/g, '');
        if (eventCep.length === 8) {
          const supplierIds = Object.keys(optimizedData);
          const { perSupplier } = await calculateFreightForEvent(
            eventCep,
            supplierIds,
            isOrderJourney ? 'order' : 'event',
          );

          for (const result of perSupplier) {
            if (result.matchType === 'none') {
              console.warn(`🚚 [Freight] No coverage match for supplier ${result.supplierId} and CEP ${eventCep}`);
              outOfArea = true;
              continue;
            }
            const supplierData = optimizedData[result.supplierId];
            deliveryData.push({
              category: 'frete',
              supplier_id: result.supplierId,
              supplier_name: (supplierData as any)?.name || 'Fornecedor',
              region_name: result.regionName,
              value: result.freight,
              date: eventDate,
            });
          }
        }
      }
    } catch (freightErr) {
      console.error('Error calculating freight:', freightErr);
    }

    
    // Extrair event_hour_time para popular party_time_supplier em eventos não manuais
    const extractedEventHourTime = deliveryData
      .filter(item => item.event_hour_time)
      .map(item => item.event_hour_time)[0] || null;
    
    
    return { deliveryData, eventHourTime: extractedEventHourTime, eventHourIsMax, outOfArea };
  };

  // Upsert event_location table with address data
  const upsertEventLocation = async (eventId: string) => {
    try {
      const locationData = {
        event_id: eventId,
        address_location: endereco,
        address_number: numero,
        address_city: cidade,
        address_state: normalizeRegionCode(estado),
        address_code: formatCepForStorage(cep),
        adress_complement: complemento,
        Adress_neighborhood: bairro,
      };

      // Check if record exists
      const { data: existing } = await supabase
        .from('event_location')
        .select('id')
        .eq('event_id', eventId)
        .maybeSingle();

      if (existing) {
        const { error: updateErr } = await supabase
          .from('event_location')
          .update(locationData)
          .eq('id', existing.id);
        if (updateErr) console.error('Error updating event_location:', updateErr);
      } else {
        const { error: insertErr } = await supabase
          .from('event_location')
          .insert({ id: crypto.randomUUID(), ...locationData });
        if (insertErr) console.error('Error inserting event_location:', insertErr);
      }
    } catch (error) {
      console.error('Error saving event_location:', error);
    }
  };

  const handleFestaSubmit = async () => {
    // Validar todos os campos obrigatórios
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const validSimulationId = isValidUuid(simulationId) ? simulationId : null;
      const validEventIdParam = isValidUuid(eventIdParam) ? eventIdParam : null;
      const sourceEventId = validSimulationId || validEventIdParam;

      if (!sourceEventId) {
        toast.error('Identificador inválido para buscar os dados do evento. Refaça a simulação.');
        return;
      }

      // 1. Verificar e corrigir vinculação da simulação ao usuário (somente quando há simulationId válido E usuário logado)
      if (validSimulationId && user) {
        const linkingSuccess = await linkAnonymousSimulationByIdToUser(validSimulationId, user.id);
        if (!linkingSuccess) {
          console.warn('⚠️ Failed to link simulation to user, but continuing...');
        }
      }

      // 2. Buscar dados da simulação/evento base
      const BASE_EVENT_COLUMNS =
        'event_date, calculated_total, menu, session_info, user_id, guests, delivery_fee, total_value, discount_value, discount_code';

      let {
        data: simulationData,
        error: simError
      } = await (supabase as any)
        .from('events')
        .select(BASE_EVENT_COLUMNS)
        .eq('id', sourceEventId)
        .maybeSingle();

      // A proposta pode pertencer a outra sessão anônima (link aberto em outro
      // aparelho/aba). Nesse caso a RLS devolve vazio: pedimos a transferência
      // de posse e tentamos ler novamente uma única vez.
      if (!simulationData && !simError) {
        const claim = await claimSharedQuote({ eventId: sourceEventId });
        if (claim.success) {
          const retry = await (supabase as any)
            .from('events')
            .select(BASE_EVENT_COLUMNS)
            .eq('id', sourceEventId)
            .maybeSingle();
          simulationData = retry.data;
          simError = retry.error;
        } else if (claim.error === 'OWNER_IS_REGISTERED_USER') {
          toast.error(claim.message || 'Esta proposta pertence a outra conta. Faça login para continuar.');
          return;
        }
      }

      if (simError) {
        console.error('Error fetching simulation/event base:', simError);
        toast.error('Erro ao buscar dados da simulação');
        return;
      }
      if (!simulationData) {
        toast.error('Não conseguimos recuperar sua proposta. Volte ao cardápio e refaça a seleção.');
        return;
      }

      // 3. Validar se a simulação pertence ao usuário atual (apenas quando logado)
      if (user && simulationData.user_id && simulationData.user_id !== user.id) {
        console.error('❌ Simulation belongs to different user:', { 
          simulationUserId: simulationData.user_id, 
          currentUserId: user.id 
        });
        toast.error('Esta simulação não pertence a você. Por favor, faça uma nova simulação.');
        return;
      }

      // 4. Se a simulação ainda é anônima e o usuário está logado, tentar vincular novamente
      if (validSimulationId && !simulationData.user_id && user) {
        const finalLinkAttempt = await linkAnonymousSimulationByIdToUser(validSimulationId, user.id);
        if (!finalLinkAttempt) {
          console.error('❌ Final linking attempt failed');
          toast.error('Erro ao vincular simulação ao seu usuário. Tente fazer uma nova simulação.');
          return;
        }
      }


      // 2. Usar código do evento existente (da jornada de compra) ou gerar novo como fallback
      const existingCode = eventCode || sessionStorage.getItem('ameup_event_code');
      const finalEventCode = existingCode || await generateEventCode();
      setEventCode(finalEventCode);

      // 3. Gerar dados de entrega dos fornecedores
      
      // 🛡️ SAFE UNION: Merge simulation data with current form data to preserve "salgados"
      let finalSelectedSuppliers: any = simulationData.menu || {};
      
      // Type guard for simulation data — parse strings, but PRESERVE arrays (cart format)
      if (typeof finalSelectedSuppliers === 'string') {
        try {
          finalSelectedSuppliers = JSON.parse(finalSelectedSuppliers);
        } catch (e) {
          console.error('Error parsing simulation selected_suppliers:', e);
          finalSelectedSuppliers = {};
        }
      }
      
      // Only reset to {} when value is neither object nor array (invalid format)
      if (finalSelectedSuppliers === null || (typeof finalSelectedSuppliers !== 'object')) {
        finalSelectedSuppliers = {};
      }
      
      const menuIsArray = Array.isArray(finalSelectedSuppliers);
      
      if (menuIsArray) {
        // Cart format (array of items) — keep intact, do NOT merge with category-based form data
        if ((finalSelectedSuppliers as any[]).length === 0 && selectedSuppliers && Object.keys(selectedSuppliers).length > 0) {
          // Empty array fallback to form data
          finalSelectedSuppliers = { ...selectedSuppliers };
        } else {
        }
      } else if (selectedSuppliers && Object.keys(selectedSuppliers).length > 0) {
        // Both are objects — perform safe union (simulation data takes priority)
        const mergedSuppliers = { ...selectedSuppliers, ...(finalSelectedSuppliers as any) };
        
        // Special handling for "salgados" category preservation
        const formSalgados = (selectedSuppliers as any).salgados;
        const simSalgados = (finalSelectedSuppliers as any).salgados;
        
        if (formSalgados && !simSalgados) {
          mergedSuppliers.salgados = formSalgados;
        }
        
        finalSelectedSuppliers = mergedSuppliers;
      }
      
      // 💰 Recompute menu subtotal from cart array items (totalPrice) for total_value safety net
      let menuSubtotal = 0;
      if (Array.isArray(finalSelectedSuppliers)) {
        menuSubtotal = (finalSelectedSuppliers as any[]).reduce((sum, item) => {
          const v = Number(item?.totalPrice ?? item?.value ?? item?.price ?? 0);
          return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
      }
      
      const deliveryResult = await generateDeliveryData(
        finalSelectedSuppliers,
        finalEventCode,
        simulationEventDate || simulationData.event_date,
        supplierTimings,
        deliveryAddress,
        confirmCurrentAddress,
        eventGuests || simulationData.guests,
        simulationData.calculated_total // ✅ Passar eventTotalValue
      );
      
      // 🚫 Endereço fora da área de entrega do tipo do pedido (encomenda/evento):
      // não seguir com frete zerado.
      if ((deliveryResult as any).outOfArea) {
        toast.error(
          'Esse endereço está fora da área de entrega deste fornecedor. Altere o endereço ou escolha retirar no local, se disponível.',
        );
        return;
      }
      const deliverySupplierData = deliveryResult.deliveryData;
      const extractedEventHourTime = deliveryResult.eventHourTime;
      const extractedEventHourIsMax = Boolean((deliveryResult as any).eventHourIsMax);

      // 4. Update the EXISTING parent event (sourceEventId) instead of creating a new one
      // Calculate delivery_fee from delivery data (freight items)
      let freightTotal = deliverySupplierData
        .filter((item: any) => {
          const cat = String(item.category || '').toLowerCase();
          return cat === 'frete' || cat === 'shipping' || cat === 'entrega';
        })
        .reduce((sum: number, item: any) => sum + (item.value || 0), 0);
      
      // 🚚 FALLBACK: If calculated freight is 0 but database already has a value, PRESERVE it.
      // Helps when freight was manually set in DB or comes from a previous step.
      const existingFreight = Number(simulationData.delivery_fee || 0);
      if (freightTotal === 0 && existingFreight > 0) {
        freightTotal = existingFreight;
      }

      // 💰 Compute totals respecting the formula:
      //   calculated_total (items subtotal) + delivery_fee - discount_value = total_value
      const itemsSubtotal = menuSubtotal || 0;
      // Cupom de frete grátis: o desconto é o próprio frete recalculado.
      const shippingCouponApplied = simulationData.discount_code
        ? await fetchShippingCoupon(simulationData.discount_code, eventSupplierId)
        : null;
      const freeShippingApplied = !!shippingCouponApplied;
      const existingDiscount = shippingCouponApplied
        ? calculateCouponDiscount(shippingCouponApplied, { subtotal: menuSubtotal || 0, freight: freightTotal || 0 })
        : Number(simulationData.discount_value || 0);
      const computedTotal = itemsSubtotal + (freightTotal || 0) - existingDiscount;
      const existingTotal = Number(simulationData.total_value || simulationData.calculated_total || 0);
      const finalTotalValue = computedTotal > 0 ? computedTotal : existingTotal;
      const finalCalculatedTotal = itemsSubtotal > 0
        ? itemsSubtotal
        : Number(simulationData.calculated_total || 0);

      const baseEventData: Record<string, any> = {
        ...(user ? { user_id: user.id } : {}),
        // Com "Outros", grava o que o cliente descreveu (ex.: "Pré natal") no lugar de "Outros".
        event_type: resolveEventTypeForSave(tipoEvento, tipoEventoOutros),
        event_date: simulationEventDate || simulationData.event_date,
        guests: eventGuests || simulationData.guests,
        payment_method: null,
        status: 'Aguardando Pagamento',
        geral_status: 'Aguardando Pagamento',
        cep: formatCepForStorage(cep),
        party_time: partyTimeDuration || undefined,
        event_hour: horarioInicio || undefined,
        // Congela no evento se a duração contratada é um teto ("até X horas")
        ...(extractedEventHourIsMax ? { event_hour_is_max: true } : {}),
        menu: finalSelectedSuppliers,
        event_code: finalEventCode,
        delivery_supplier: deliverySupplierData,
        delivery_fee: freightTotal,
        ...(freeShippingApplied ? { discount_value: existingDiscount } : {}),
        ...(finalTotalValue > 0 ? {
          total_value: finalTotalValue,
          event_value_total: String(finalTotalValue)
        } : {}),
        ...(finalCalculatedTotal > 0 ? {
          calculated_total: finalCalculatedTotal
        } : {})
      };

      // Always update the existing sourceEventId (parent event) — never create a new one
      const updateData = getAuditFieldsForUpdate(baseEventData);
      const { error: updateError } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', sourceEventId);
      if (updateError) {
        console.error('Error updating event:', updateError);
        toast.error('Erro ao atualizar evento');
        return;
      }

      // Upsert event_location for the event
      await upsertEventLocation(sourceEventId);

      // Persist client data into event_client_info via edge function (service role
      // bypasses RLS for guest checkouts). This MUST succeed before navigating to
      // checkout — payment hooks depend on this record to identify the customer.
      const trimmedName = nomeCompleto.trim();
      const trimmedEmail = (emailCliente.trim() || user?.email || '').trim();
      const digitsPhone = telefoneCliente.replace(/\D/g, '');

      if (!trimmedName || !trimmedEmail || !digitsPhone) {
        toast.error('Preencha nome, e-mail e telefone antes de continuar.');
        setLoading(false);
        return;
      }

      const emailProblem = getEmailProblem(trimmedEmail);
      if (emailProblem) {
        setErrors((prev: any) => ({ ...prev, emailCliente: true }));
        toast.error(emailProblem);
        setLoading(false);
        return;
      }

      if (!isValidCpfCnpj(cpfCnpj)) {
        setErrors((prev: any) => ({ ...prev, cpfCnpj: true }));
        toast.error(cpfCnpjErrorMessage(cpfCnpj));
        setLoading(false);
        return;
      }

      // CNPJ: a inscrição estadual (ou isento / não contribuinte) é obrigatória; sem ela a SEFAZ recusa a nota fiscal.
      const isCnpjDocument = cpfCnpj.replace(/\D/g, '').length === 14;
      if (isCnpjDocument && !parseStateRegistration(inscricaoEstadual)) {
        setErrors((prev: any) => ({ ...prev, inscricaoEstadual: true }));
        toast.error('Informe a inscrição estadual da empresa (ou marque isento / não contribuinte).');
        setLoading(false);
        return;
      }

      try {
        const { data: saveResp, error: saveErr } = await supabase.functions.invoke(
          'save-event-client-info',
          {
            body: {
              eventId: sourceEventId,
              clientName: trimmedName,
              clientEmail: trimmedEmail,
              clientPhone: digitsPhone,
              clientCpf: cpfCnpj.replace(/\D/g, ''),
              ...(isCnpjDocument ? { clientStateRegistration: inscricaoEstadual.trim() } : {}),
            },
          }
        );

        if (saveErr || !saveResp?.success) {
          console.error('❌ Failed to persist event_client_info:', saveErr || saveResp);
          toast.error(
            saveResp?.error ||
              'Não foi possível salvar seus dados. Tente novamente em instantes.'
          );
          setLoading(false);
          return;
        }

        try {
          sessionStorage.setItem(
            `checkout_client_info_${sourceEventId}`,
            JSON.stringify({
              fullName: trimmedName,
              email: trimmedEmail,
              phone: digitsPhone,
              cpf: cpfCnpj.replace(/\D/g, ''),
              savedAt: new Date().toISOString(),
            })
          );
        } catch (storageErr) {
          console.warn('⚠️ Could not cache checkout client info locally:', storageErr);
        }
      } catch (clientErr) {
        console.error('❌ Could not persist client info:', clientErr);
        toast.error('Não foi possível salvar seus dados. Tente novamente em instantes.');
        setLoading(false);
        return;
      }

      // Persiste dados do cliente no profile (inclui sessões anônimas/convidado).
      // A trigger `sync_client_info_to_profile` no banco é a rede de segurança.
      if (user) {
        try {
          const profilePatch: Record<string, string> = {
            updated_at: new Date().toISOString(),
          };
          if (trimmedName) profilePatch.full_name = trimmedName;
          if (digitsPhone) profilePatch.phone = digitsPhone;
          if (trimmedEmail) profilePatch.email = trimmedEmail;
          const digitsDoc = cpfCnpj.replace(/\D/g, '');
          if (digitsDoc) profilePatch.document_number = digitsDoc;

          await (supabase as any)
            .from('profile')
            .update(profilePatch)
            .eq('id', user.id);
        } catch (e) {
          console.warn('⚠️ Could not update profile doc:', e);
        }
      }


      // Update child events status too
      await supabase
        .from('events')
        .update({ 
          status: 'Aguardando Pagamento', 
          geral_status: 'Aguardando Pagamento',
          updated_at: new Date().toISOString() 
        })
        .eq('parent_event_id', sourceEventId);

      // Redirect to checkout with the same event ID (force canonical host)
      navigate(`/checkout/${sourceEventId}?simulationId=${sourceEventId}`);
    } catch (error) {
      console.error('❌ [InfosFesta] Error saving party info:', error);
      toast.error('Erro ao salvar informações da festa');
    } finally {
      setLoading(false);
    }
  };

  // Re-compute theme when themeReady changes (supplier customization loaded from DB)
  const theme = useMemo(() => getSupplierTheme(), [themeReady]);
  useSupplierFavicon({ logoUrl: theme.logoUrl, supplierName: theme.supplierName });
  const colors = useMemo(() => getSupplierColors(), [themeReady]);
  
  const handleBackToEventDetails = async () => {
    const params = new URLSearchParams();
    
    if (eventIdParam) {
      params.set('id', eventIdParam);
    }
    
    if (simulationId) {
      params.set('simulationId', simulationId);
      
      try {
        await (supabase as any).from('events').update({
          status: 'simulation'
        }).eq('id', simulationId);
      } catch (error) {
        console.error('Error updating simulation status:', error);
      }
    }
    
    navigate('/login');
  };

  // Build customization object for StoreNavTabs
  const storeCustomization = theme.isDigitalMenu ? {
    headerColor: theme.headerColor || colors.headerColor,
    buttonTextColor: colors.buttonTextColor || '#FFFFFF',
  } : null;

  const handleBackToCart = async () => {
    // Force immediate save of current form data before navigating away
    // Se a gravação falhar ou demorar, o cliente volta mesmo assim (antes o botão "Voltar" parecia não fazer nada).
    if (targetEventId) {
      const cleanCep = cep.replace(/\D/g, '');
      try {
        await Promise.race([
          Promise.all([
            saveEventPatch({
              event_type: tipoEvento ? resolveEventTypeForSave(tipoEvento, tipoEventoOutros) : undefined,
              event_hour: horarioInicio || undefined,
              cep: cleanCep || undefined,
            }),
            upsertEventLocationPatch({
              address_location: endereco || undefined,
              address_number: numero || undefined,
              adress_complement: complemento || undefined,
              Adress_neighborhood: bairro || undefined,
              address_city: cidade || undefined,
              address_state: estado ? normalizeRegionCode(estado) : undefined,
              address_code: cleanCep || undefined,
            }),
          ]),
          new Promise((resolve) => setTimeout(resolve, 4000)),
        ]);
      } catch (saveErr) {
        console.warn('[InfosFesta] Não foi possível salvar antes de voltar:', saveErr);
      }
    }
    // Sync edited data back to cart sessionStorage
    try {
      const raw = sessionStorage.getItem('supplier_cart_items');
      if (raw) {
        const items = JSON.parse(raw);
        const cleanCep = cep.replace(/\D/g, '');
        const updated = items.map((item: any) => ({
          ...item,
          ...(cleanCep ? { cep: cleanCep } : {}),
          ...(simulationEventDate ? { date: simulationEventDate } : {}),
          ...(eventGuests ? { guests: eventGuests } : {}),
        }));
        sessionStorage.setItem('supplier_cart_items', JSON.stringify(updated));
      }
      const slug = theme.supplierSlug || '';
      if (slug) {
        sessionStorage.setItem(`store_filters_${slug}`, JSON.stringify({
          cep: cep.replace(/\D/g, '') || '',
          date: simulationEventDate || null,
          guests: eventGuests || ''
        }));
      }
      window.dispatchEvent(new Event('cart-filters-updated'));
    } catch (e) {
      console.error('Error syncing cart filters on back:', e);
    }
    sessionStorage.setItem('open-cart-on-load', 'true');
    const slug = theme.supplierSlug || loadSupplierCustomization()?.supplierSlug || sessionStorage.getItem('ameup_last_supplier_slug') || '';
    navigate(slug ? `/loja/${slug}` : getHomeFallbackPath());
  };

  return <div className="min-h-screen flex flex-col bg-background">
      {theme.isDigitalMenu ? (
        <StoreNavTabs 
          activeTab="inicio"
          showBackButton={true}
          supplierName={theme.supplierName || ''}
          supplierSlug={theme.supplierSlug || ''}
          customization={storeCustomization as any}
          eventCode={eventCode}
          onBack={handleBackToCart}
          backLabel="Voltar ao carrinho"
        />
      ) : (
        <>
          <Navbar />
          <div className="container-custom pt-6">
            <Button variant="outline" onClick={handleBackToCart} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Voltar ao carrinho
            </Button>
          </div>
        </>
      )}
      
      {/* Branded hero section for digital menu */}

      <main className={`flex-grow ${theme.isDigitalMenu ? 'relative z-10 pb-8 pt-10' : 'py-16 bg-gradient-to-b from-accent to-white'}`}>
        <div className="container-custom">

          <div className={`${currentStep === 'login' ? 'max-w-md' : 'max-w-4xl'} mx-auto flex flex-col gap-8`}>
            
            {/* Loading simulation data */}
            {loadingSimulation && <Card className="border-none shadow-lg">
                <CardContent className="py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: colors.buttonColor }}></div>
                  <p className="text-gray-600">Carregando os dados do seu pedido...</p>
                </CardContent>
              </Card>}


            {/* Seção de Login - mostra primeiro se requireLogin=true */}
            {currentStep === 'login' && <Card className="border-none shadow-lg rounded-2xl">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold">{isLoginView ? 'Bem-vindo' : 'Cadastre-se grátis'}</CardTitle>
                   <p className="text-muted-foreground">{isLoginView ? 'Insira seus dados para avançar para os detalhes da sua festa' : 'Preencha seus dados para criar sua conta'}</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAuthSubmit} className="space-y-4">
                    {!isLoginView && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="name">Nome completo</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <Input id="name" type="text" placeholder="Seu nome completo" className={`pl-10 ${formErrors.fullName ? 'border-destructive' : ''}`} value={fullName} onChange={e => { setFullName(e.target.value); setFormErrors(p => ({...p, fullName: ''})); }} />
                          </div>
                          {formErrors.fullName && <p className="text-xs text-destructive">{formErrors.fullName}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="signup-phone">Celular</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <Input id="signup-phone" type="tel" placeholder="(11) 99999-9999" className={`pl-10 ${formErrors.phone ? 'border-destructive' : ''}`} value={phone} onChange={e => { setPhone(formatPhone(e.target.value)); setFormErrors(p => ({...p, phone: ''})); }} />
                          </div>
                          {formErrors.phone && <p className="text-xs text-destructive">{formErrors.phone}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="signup-cpf">CPF</Label>
                          <div className="relative">
                            <CreditCard className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <Input id="signup-cpf" type="text" placeholder="000.000.000-00" className={`pl-10 ${formErrors.cpf ? 'border-destructive' : ''}`} value={cpf} onChange={e => { setCpfField(formatCPF(e.target.value)); setFormErrors(p => ({...p, cpf: ''})); }} />
                          </div>
                          {formErrors.cpf && <p className="text-xs text-destructive">{formErrors.cpf}</p>}
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="email" type="email" placeholder="seu@email.com" className={`pl-10 ${formErrors.email ? 'border-destructive' : ''}`} value={email} onChange={e => { setEmail(e.target.value); setFormErrors(p => ({...p, email: ''})); }} />
                      </div>
                      {formErrors.email && <p className="text-xs text-destructive">{formErrors.email}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">{isLoginView ? 'Senha' : 'Cadastre uma senha'}</Label>
                        {isLoginView && <Link to="/esqueci-senha" className="text-sm hover:opacity-80" style={{ color: colors.accentColor }}>
                            Esqueceu a senha?
                          </Link>}
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="password" type="password" placeholder="••••••••" className={`pl-10 ${formErrors.password ? 'border-destructive' : ''}`} value={password} onChange={e => { setPassword(e.target.value); setFormErrors(p => ({...p, password: ''})); }} />
                      </div>
                      {formErrors.password && <p className="text-xs text-destructive">{formErrors.password}</p>}
                      {!isLoginView && !formErrors.password && <p className="text-xs text-muted-foreground">Mínimo de 6 caracteres</p>}
                    </div>
                    
                    {isLoginView && <div className="flex items-center space-x-2">
                        <Checkbox id="remember" checked={rememberMe} onCheckedChange={checked => setRememberMe(!!checked)} />
                        <Label htmlFor="remember" className="text-sm">
                          Lembrar de mim
                        </Label>
                      </div>}
                    
                    <Button type="submit" className="w-full hover:opacity-90 transition-opacity" style={{ backgroundColor: colors.buttonColor, color: colors.buttonTextColor }} disabled={loading}>
                      {loading ? isLoginView ? "Entrando..." : "Criando conta..." : isLoginView ? "Entrar" : "Cadastrar"}
                    </Button>
                  </form>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">ou entre com</span></div>
                  </div>

                  <div className="flex justify-center">
                    <Button variant="outline" type="button" className="flex items-center justify-center w-full" onClick={() => handleSocialLogin('google')} disabled={loading}>
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z" />
                        <path fill="#34A853" d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z" />
                        <path fill="#4A90E2" d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z" />
                        <path fill="#FBBC05" d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 1.92.445 3.73 1.237 5.335l4.04-3.067Z" />
                      </svg>
                      Google
                    </Button>
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      {isLoginView ? "Ainda não tem uma conta?" : "Já tem uma conta?"}{" "}
                      <button type="button" onClick={() => { setIsLoginView(!isLoginView); setFormErrors({}); }} className="font-medium hover:opacity-80" style={{ color: colors.accentColor }}>
                        {isLoginView ? "Cadastre-se grátis" : "Entre"}
                      </button>
                    </p>
                  </div>
                </CardContent>
              </Card>}

            {/* Formulário da Festa - mostra após login */}
            {currentStep === 'form' && !loadingSimulation && (() => {
              // Stepper config — dinâmico: omite "Endereço" quando retirada no local
              const seusDadosOk = !!(nomeCompleto.trim() && isValidCpfCnpj(cpfCnpj) && (cpfCnpj.replace(/\D/g, '').length !== 14 || parseStateRegistration(inscricaoEstadual)) && isValidTelefone(telefoneCliente) && (isValidEmail(emailCliente) || !!user?.email));
              const addressOk = isPickup
                ? !!pickupSupplierAddress
                : !!(endereco.trim() && cep.trim() && cidade.trim() && estado.trim() && numero.trim());
              const timeOk = !!(simulationEventDate && horarioInicio && timeValidationResult.valid);
              // Mesma fonte de verdade usada pelo auto-advance (journeyStepKeys)
              const stepLabelMap: Record<JourneyStepKey, string> = {
                event: 'Evento',
                address: isPickup ? 'Retirada no local' : (journeyLocationLabel || 'Endereço'),
                time: journeyDateLabel || 'Data e Horário',
                data: 'Seus Dados',
              };
              const stepDoneMap: Record<JourneyStepKey, boolean> = {
                event: !!tipoEvento && !outrosPendente,
                address: addressOk,
                time: timeOk,
                data: seusDadosOk,
              };
              const stepKeys = journeyStepKeys;
              const totalSteps = stepKeys.length;
              const stepLabels = stepKeys.map(k => stepLabelMap[k]);
              const stepCompletion = stepKeys.map(k => stepDoneMap[k]);

              const activeStep = Math.max(1, Math.min(carouselStep || 1, totalSteps));
              const activeKey = stepKeys[activeStep - 1];
              const canGoNext = stepCompletion[activeStep - 1];
              // A rolagem é feita pelo efeito que observa `carouselStep`,
              // depois que o novo passo já foi renderizado.
              const goNext = () => {
                if (activeStep < totalSteps) {
                  setCarouselStep(activeStep + 1);
                }
              };
              const goPrev = () => {
                if (activeStep > 1) {
                  setCarouselStep(activeStep - 1);
                }
              };
              return (
              <Card ref={formRef} className="border-none shadow-none bg-transparent">
                <CardHeader className="pb-4 px-0">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="text-xl font-semibold text-foreground tracking-tight">
                      {isOrderJourney ? 'Nos informe mais alguns detalhes do seu pedido' : isServiceJourney ? 'Conte mais sobre o seu evento' : 'Conte-nos sobre sua festa'}
                    </CardTitle>
                    {/* Event summary chip for mobile/desktop - Removed guests and date as requested */}
                  </div>

                  {/* Stepper indicator horizontal */}
                  <div className="mt-4">
                    <div className="flex items-center gap-2">
                      {stepLabels.map((label, idx) => {
                        const stepNum = idx + 1;
                        const isActive = activeStep === stepNum;
                        const isDone = stepCompletion[idx] && activeStep !== stepNum;
                        const isReachable = stepNum === 1 || stepCompletion.slice(0, stepNum - 1).every(Boolean);
                        return (
                          <div key={label} className="flex items-center gap-2 flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                if (isReachable) {
                                  setCarouselStep(stepNum);
                                }
                              }}
                              disabled={!isReachable}
                              className={`flex items-center gap-2 min-w-0 ${!isReachable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-current={isActive ? 'step' : undefined}
                              aria-label={`Etapa ${stepNum}: ${label}`}
                            >
                              <div
                                className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                  isDone
                                    ? ''
                                    : isActive
                                    ? 'bg-foreground text-background ring-2 ring-offset-2 ring-foreground/20'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                                style={isDone ? { backgroundColor: colors.buttonColor, color: colors.buttonTextColor } : undefined}
                              >
                                {isDone ? (
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                ) : stepNum}
                              </div>
                              <span className={`text-xs sm:text-sm font-medium truncate hidden md:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {label}
                              </span>
                            </button>
                            {idx < stepLabels.length - 1 && (
                              <div className={`h-0.5 flex-1 rounded-full transition-colors ${stepCompletion[idx] ? '' : 'bg-muted'}`} style={stepCompletion[idx] ? { backgroundColor: colors.buttonColor } : undefined} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 sm:hidden font-medium">
                      Etapa {activeStep} de {totalSteps}: <span className="text-foreground">{stepLabels[activeStep - 1]}</span>
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-0 px-0">

                  
                  {/* ── STEP 1: Tipo de evento (ocultável pela Jornada do Cliente) ── */}
                  {!hideStepEvent && (
                  <div className={`py-6 ${activeKey === 'event' ? 'block animate-fade-in' : 'hidden'}`}>


                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">Selecione o tipo de evento.</p>
                      {eventGuests && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
                          <User className="h-4 w-4" />
                          <span>Convidados: <strong className="text-foreground">{eventGuests}</strong></span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {tiposEvento.map(tipo => {
                          const isSelected = tipoEvento === tipo;
                          return (
                            <button
                              key={tipo}
                              type="button"
                              onClick={() => {
                                setTipoEvento(tipo);
                                if (!festaNome.trim()) setFestaNome(`Minha ${tipo}`);
                                if (errors.tipoEvento) setErrors((prev: any) => ({ ...prev, tipoEvento: false }));
                                // Autosave event_type immediately
                                saveEventPatch({ event_type: resolveEventTypeForSave(tipo, tipoEventoOutros) });
                                // Auto-advance (em "Outros" espera o cliente descrever o evento)
                                if (tipo !== 'Outros') setTimeout(() => setCarouselStep(prev => prev + 1), 350);
                              }}
                              className={`relative flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-xl border transition-all duration-200 ${
                                isSelected
                                  ? 'shadow-sm'
                                  : 'border-border bg-card hover:border-muted-foreground/40'
                            }`} style={isSelected ? { borderColor: colors.buttonColor, backgroundColor: `${colors.buttonColor}10` } : undefined}
                            >
                              {(() => {
                                const TipoIcon =
                                  tipo === 'Aniversário' ? Cake :
                                  tipo === 'Confraternização' ? PartyPopper :
                                  tipo === 'Chá de Panela' ? ChefHat :
                                  tipo === 'Chá de Cozinha' ? UtensilsCrossed :
                                  tipo === 'Batizado' ? Church :
                                  tipo === 'Coffee Break' ? Coffee : Sparkles;
                                return (
                                  <span
                                    className="flex h-11 w-11 items-center justify-center rounded-full transition-colors"
                                    style={isSelected
                                      ? { backgroundColor: colors.buttonColor, color: colors.buttonTextColor }
                                      : { backgroundColor: `${colors.buttonColor}1A`, color: colors.buttonColor }}
                                  >
                                    <TipoIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                                  </span>
                                );
                              })()}
                              <span className="text-xs font-medium text-foreground">{tipo}</span>
                              {isSelected && (
                                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.buttonColor }}>
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} style={{ color: colors.buttonTextColor }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {tipoEvento === 'Outros' && (
                        <div className="space-y-1.5">
                          <label htmlFor="tipo-evento-outros" className="text-sm font-medium text-foreground">
                            Qual é o tipo de evento? <span className="text-destructive">*</span>
                          </label>
                          <input
                            id="tipo-evento-outros"
                            type="text"
                            maxLength={80}
                            autoFocus
                            value={tipoEventoOutros}
                            onChange={(e) => {
                              updateTipoEventoOutros(e.target.value);
                              if (errors.tipoEvento) setErrors((prev: any) => ({ ...prev, tipoEvento: false }));
                            }}
                            onBlur={() => {
                              const custom = formatCustomEventType(tipoEventoOutros);
                              if (custom) saveEventPatch({ event_type: custom });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && tipoEventoOutros.trim()) {
                                e.preventDefault();
                                saveEventPatch({ event_type: formatCustomEventType(tipoEventoOutros) });
                                setCarouselStep(prev => prev + 1);
                              }
                            }}
                            placeholder="Ex.: Formatura, Confraternização da empresa, Inauguração"
                            className={`w-full h-11 rounded-lg border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-offset-1 ${errors.tipoEvento && !tipoEventoOutros.trim() ? 'border-destructive' : 'border-border'}`}
                          />
                        </div>
                      )}
                      {errors.tipoEvento && (
                        <p className="text-destructive text-sm">
                          {tipoEvento === 'Outros' ? 'Informe qual é o tipo de evento' : 'Selecione o tipo de evento'}
                        </p>
                      )}
                    </div>
                  </div>
                  )}


                  {/* ── STEP 2: Endereço ── */}
                  <div className={`py-6 ${activeKey === 'address' ? 'block animate-fade-in' : 'hidden'}`}>
                    {isPickup ? (
                      <div className="space-y-5">
                        <p className="text-sm text-muted-foreground">Retirar o pedido em:</p>

                        {pickupSupplierAddress ? (
                          <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                              Endereço de retirada
                            </p>
                            <p className="text-sm font-medium text-foreground">
                              {[
                                pickupSupplierAddress.address_name,
                                pickupSupplierAddress.address_number ? `nº ${pickupSupplierAddress.address_number}` : null,
                                pickupSupplierAddress.address_complement,
                              ].filter(Boolean).join(', ') || pickupSupplierAddress.full_address || '—'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {[
                                pickupSupplierAddress.city,
                                pickupSupplierAddress.state,
                              ].filter(Boolean).join(' - ')}
                              {pickupSupplierAddress.address_code
                                ? ` • CEP ${String(pickupSupplierAddress.address_code).replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2')}`
                                : ''}
                            </p>
                            <p className="text-[11px] text-muted-foreground pt-1">
                              Este endereço é definido pelo fornecedor e não pode ser alterado.
                            </p>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-2">
                            <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                            <div className="h-4 w-64 rounded bg-muted animate-pulse" />
                          </div>
                        )}

                        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex gap-3 items-start">
                          <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-foreground">Retirada no local — sem entrega</p>
                            <p className="text-xs text-muted-foreground">
                              Você retira o pedido no endereço acima, na data e horário escolhidos. O fornecedor não fará entrega.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <div className="space-y-5">
                      <p className="text-sm text-muted-foreground">
                        {journeyLocationLabel
                          ? `Informe o endereço — ${journeyLocationLabel}`
                          : isOrderJourney
                            ? 'Onde devemos entregar o seu pedido'
                            : 'Onde será seu evento?'}
                      </p>

                      


                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className={`text-[10px] font-bold uppercase tracking-wider ${errors.cep ? 'text-destructive' : 'text-muted-foreground'}`}>
                            CEP {loadingCep && <span className="text-[9px] font-normal">(buscando...)</span>}
                          </Label>
                          <Input 
                            placeholder="00000-000" 
                            value={cep} 
                            onChange={e => handleCepChange(e.target.value)} 
                            className={`h-11 bg-muted/20 border-border/60 focus:bg-background transition-all ${errors.cep ? 'border-destructive ring-destructive/20' : ''}`} 
                            disabled={loadingCep} 
                          />
                          {isValidatingDelivery && (
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                              <Loader2 className="h-3 w-3 animate-spin" /> Verificando área de entrega…
                            </p>
                          )}
                          {!isValidatingDelivery && deliveryMessage && (
                            <p className={`text-[11px] mt-1 flex items-center gap-1 ${
                              deliveryMessage.type === 'error'
                                ? 'text-destructive'
                                : deliveryMessage.type === 'warn'
                                ? 'text-amber-600'
                                : 'text-emerald-600'
                            }`}>
                              <Truck className="h-3 w-3" /> {deliveryMessage.text}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Bairro</Label>
                          <Input 
                            placeholder="Ex: Centro" 
                            value={bairro} 
                            onChange={e => {
                              setBairro(e.target.value);
                              debouncedUpsertLocationPatch({ Adress_neighborhood: e.target.value });
                            }} 
                            className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all" 
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className={`text-[10px] font-bold uppercase tracking-wider ${errors.endereco ? 'text-destructive' : 'text-muted-foreground'}`}>Endereço</Label>
                        <Input 
                          placeholder="Rua, avenida..." 
                          value={endereco} 
                          onChange={e => {
                            setEndereco(e.target.value);
                            if (errors.endereco && e.target.value.trim()) setErrors((prev: any) => ({ ...prev, endereco: false }));
                            debouncedUpsertLocationPatch({ address_location: e.target.value });
                          }} 
                          className={`h-11 bg-muted/20 border-border/60 focus:bg-background transition-all ${errors.endereco ? 'border-destructive ring-destructive/20' : ''}`} 
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className={`text-[10px] font-bold uppercase tracking-wider ${errors.numero ? 'text-destructive' : 'text-muted-foreground'}`}>Número</Label>
                          <Input 
                            placeholder="123" 
                            value={numero} 
                            onChange={e => {
                              setNumero(e.target.value);
                              if (errors.numero && e.target.value.trim()) setErrors((prev: any) => ({ ...prev, numero: false }));
                              debouncedUpsertLocationPatch({ address_number: e.target.value });
                            }} 
                            className={`h-11 bg-muted/20 border-border/60 focus:bg-background transition-all ${errors.numero ? 'border-destructive ring-destructive/20' : ''}`} 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Complemento</Label>
                          <Input 
                            placeholder="Apto, casa, etc." 
                            value={complemento} 
                            onChange={e => {
                              setComplemento(e.target.value);
                              debouncedUpsertLocationPatch({ adress_complement: e.target.value });
                            }} 
                            className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all" 
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className={`text-[10px] font-bold uppercase tracking-wider ${errors.cidade ? 'text-destructive' : 'text-muted-foreground'}`}>Cidade</Label>
                          <Input 
                            placeholder="São Paulo" 
                            value={cidade} 
                            onChange={e => {
                              setCidade(e.target.value);
                              if (errors.cidade && e.target.value.trim()) setErrors((prev: any) => ({ ...prev, cidade: false }));
                              debouncedUpsertLocationPatch({ address_city: e.target.value });
                            }} 
                            className={`h-11 bg-muted/20 border-border/60 focus:bg-background transition-all ${errors.cidade ? 'border-destructive ring-destructive/20' : ''}`} 
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className={`text-[10px] font-bold uppercase tracking-wider ${errors.estado ? 'text-destructive' : 'text-muted-foreground'}`}>Estado</Label>
                          <Input 
                            placeholder="SP" 
                            value={estado} 
                            onChange={e => {
                              setEstado(e.target.value);
                              if (errors.estado && e.target.value.trim()) setErrors((prev: any) => ({ ...prev, estado: false }));
                              debouncedUpsertLocationPatch({ address_state: normalizeRegionCode(e.target.value) });
                            }}
                            className={`h-11 bg-muted/20 border-border/60 focus:bg-background transition-all ${errors.estado ? 'border-destructive ring-destructive/20' : ''}`}
                          />
                        </div>
                      </div>

                      {/* Endereço de faturamento (nota fiscal) — nem sempre é o mesmo da festa */}
                      <div className="pt-2 border-t border-border/60 space-y-4">
                        <div className="flex items-start gap-3 pt-4">
                          <Checkbox
                            id="billing-same-as-event"
                            checked={billingUseEventAddress}
                            onCheckedChange={(checked) => handleBillingUseEventAddressChange(checked === true)}
                            className="mt-0.5"
                          />
                          <Label htmlFor="billing-same-as-event" className="text-sm font-normal leading-snug cursor-pointer">
                            Usar este mesmo endereço na nota fiscal
                          </Label>
                        </div>

                        {!billingUseEventAddress && (
                          <div className="space-y-4 animate-fade-in">
                            <p className="text-xs text-muted-foreground">
                              Informe o endereço que deve aparecer na nota fiscal deste pedido.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                  CEP do faturamento {loadingBillingCep && <span className="text-[9px] font-normal">(buscando...)</span>}
                                </Label>
                                <Input
                                  placeholder="00000-000"
                                  value={billingCep}
                                  onChange={e => handleBillingCepChange(e.target.value)}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                  disabled={loadingBillingCep}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Bairro</Label>
                                <Input
                                  placeholder="Ex: Centro"
                                  value={billingBairro}
                                  onChange={e => {
                                    setBillingBairro(e.target.value);
                                    debouncedUpsertBillingPatch({ address_neighborhood: e.target.value });
                                  }}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                />
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Endereço</Label>
                              <Input
                                placeholder="Rua, avenida..."
                                value={billingEndereco}
                                onChange={e => {
                                  setBillingEndereco(e.target.value);
                                  debouncedUpsertBillingPatch({ address_location: e.target.value });
                                }}
                                className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                              />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Número</Label>
                                <Input
                                  placeholder="123"
                                  value={billingNumero}
                                  onChange={e => {
                                    setBillingNumero(e.target.value);
                                    debouncedUpsertBillingPatch({ address_number: e.target.value });
                                  }}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Complemento</Label>
                                <Input
                                  placeholder="Apto, casa, etc."
                                  value={billingComplemento}
                                  onChange={e => {
                                    setBillingComplemento(e.target.value);
                                    debouncedUpsertBillingPatch({ address_complement: e.target.value });
                                  }}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Cidade</Label>
                                <Input
                                  placeholder="São Paulo"
                                  value={billingCidade}
                                  onChange={e => {
                                    setBillingCidade(e.target.value);
                                    debouncedUpsertBillingPatch({ address_city: e.target.value });
                                  }}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Estado</Label>
                                <Input
                                  placeholder="SP"
                                  value={billingEstado}
                                  onChange={e => {
                                    setBillingEstado(e.target.value);
                                    debouncedUpsertBillingPatch({ address_state: normalizeRegionCode(e.target.value) });
                                  }}
                                  className="h-11 bg-muted/20 border-border/60 focus:bg-background transition-all"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </div>


                  {/* ── STEP 3: Data e Horário (ocultável pela Jornada do Cliente) ── */}
                  {!progression.hasOnlyDeliveryProducts && !hideStepDatetime && (
                    <div className={`py-6 ${activeKey === 'time' ? 'block animate-fade-in' : 'hidden'}`}>

                      <div className="space-y-6">
                        <p className="text-sm text-muted-foreground">{isPickup ? 'Confirme a data e defina o horário em que você irá retirar o pedido no endereço do fornecedor.' : 'Confirme a data e defina o horário para o recebimento dos produtos ou início do serviço.'}</p>
                          
                          {/* Date & Duration row */}
                          <div className={`grid gap-4 ${partyTimeDuration ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                            {/* Event Date — segmented digits */}
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{journeyDateLabel || 'Data do Evento'}</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="w-full flex items-center gap-3 h-14 px-4 rounded-xl border border-border bg-card hover:bg-accent/50 transition-colors"
                                  >
                                    <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                                    {simulationEventDate ? (() => {
                                      try {
                                        const parsed = parseDateFromStorage(simulationEventDate);
                                        if (!parsed) return <span className="text-muted-foreground">Selecionar</span>;
                                        const day = format(parsed, 'dd');
                                        const month = format(parsed, 'MMM', { locale: ptBR }).toUpperCase();
                                        const year = format(parsed, 'yyyy');
                                        return (
                                          <div className="flex items-center gap-2">
                                            <span className="text-2xl font-bold text-foreground tabular-nums">{day}</span>
                                            <div className="flex flex-col leading-none">
                                              <span className="text-xs font-semibold text-muted-foreground">{month}</span>
                                              <span className="text-xs text-muted-foreground/70">{year}</span>
                                            </div>
                                          </div>
                                        );
                                      } catch { return <span className="text-muted-foreground">Selecionar</span>; }
                                    })() : <span className="text-muted-foreground text-sm">Selecionar data</span>}
                                    <svg className="ml-auto h-4 w-4 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={simulationEventDate ? parseDateFromStorage(simulationEventDate) || undefined : undefined}
                                    onSelect={async (date) => {
                                      if (date) {
                                        if (!validateDateOrWarn(date)) return;
                                        const formatted = formatDateForStorage(date);
                                        setSimulationEventDate(formatted);
                                        saveEventPatch({ event_date: formatted });
                                      }
                                    }}
                                    disabled={(date) => isDateDisabled(date)}
                                    className="p-3 pointer-events-auto"
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>

                            {/* Service Duration box — only if party_time exists */}
                            {partyTimeDuration && (() => {
                              const durationParts = partyTimeDuration.split(':').map(Number);
                              const durationH = durationParts[0] || 0;
                              const durationM = durationParts[1] || 0;
                              return (
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Duração do Serviço</Label>
                                  <div className="w-full flex items-center gap-3 h-14 px-4 rounded-xl border border-border bg-card">
                                    <Timer className="h-5 w-5 text-muted-foreground shrink-0" />
                                    <span className="text-2xl font-bold text-foreground tabular-nums">
                                      {durationIsMax ? 'até ' : ''}{durationH}h{durationM > 0 ? String(durationM).padStart(2, '0') : '00'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Event Time — large digital display */}
                          <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{isPickup ? 'Horário da Retirada' : isServiceJourney ? 'Horário do Início do Serviço' : 'Horário da Entrega / Início do Serviço'}</Label>
                            {/* Quick time selection grid */}
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {(simulationEventDate && availabilityData ? (() => {
                                const DEFAULT_SLOTS = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00'];
                                const ALL_DAY_SLOTS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);
                                const dObj = parseDateFromStorage(simulationEventDate);
                                if (!dObj) return DEFAULT_SLOTS;

                                const dayName = format(dObj, 'EEEE', { locale: ptBR });
                                const normalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();

                                const source = (menuType === 'selecao' ? availabilityData.deliveryHours : availabilityData.openingHours)?.horarios_funcionamento;
                                const config = source?.find(d => d.dia === normalizedDay);

                                // Atendimento o dia todo (24h)
                                if ((config as { allDay?: boolean } | undefined)?.allDay) return ALL_DAY_SLOTS;

                                if (config?.abertura && config?.fechamento) {
                                  const startH = parseInt(config.abertura.split(':')[0]);
                                  const endHRaw = parseInt(config.fechamento.split(':')[0]);
                                  // Janela que vira a madrugada (ex.: 18:00 até 02:00)
                                  const endH = endHRaw <= startH ? endHRaw + 24 : endHRaw;
                                  const slots: string[] = [];
                                  for (let h = startH; h <= endH; h++) {
                                    slots.push(`${String(h % 24).padStart(2, '0')}:00`);
                                  }
                                  return slots.length > 0 ? slots : DEFAULT_SLOTS;
                                }
                                return DEFAULT_SLOTS;
                              })() : ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00']).map((time) => {
                                // Pré-valida cada slot contra o horário de atendimento do fornecedor (com duração)
                                let slotInvalid = false;
                                let slotReason: string | undefined;
                                if (simulationEventDate) {
                                  const dObj = parseDateFromStorage(simulationEventDate);
                                  if (dObj) {
                                    const dur = partyTimeDuration ? partyTimeDuration.substring(0, 5) : undefined;
                                    const pre = validateTime(dObj, time, dur);
                                    if (!pre.valid) {
                                      slotInvalid = true;
                                      slotReason = pre.message;
                                    }
                                  }
                                }
                                return (
                                <button
                                  key={time}
                                  type="button"
                                  disabled={slotInvalid}
                                  title={slotReason}
                                  onClick={async () => {
                                    if (simulationEventDate && time) {
                                      const dateObj = parseDateFromStorage(simulationEventDate);
                                      if (dateObj) {
                                        const durationForValidation = partyTimeDuration ? partyTimeDuration.substring(0, 5) : undefined;
                                        const result = validateTime(dateObj, time, durationForValidation);
                                        setTimeValidationResult(result);
                                        if (!result.valid) {
                                          toast.error(result.message);
                                        }
                                      }
                                    } else {
                                      setTimeValidationResult({ valid: true });
                                    }
                                    setHorarioInicio(time);
                                    saveEventPatch({ event_hour: time });
                                  }}
                                  className={`h-11 rounded-xl text-sm font-semibold transition-all border-2 ${
                                    slotInvalid
                                      ? 'border-border/30 bg-muted/30 text-muted-foreground/40 cursor-not-allowed line-through'
                                      : horarioInicio === time
                                        ? 'border-primary bg-primary/10 text-primary shadow-sm scale-[1.02]'
                                        : 'border-border/50 bg-card text-foreground hover:border-primary/40 hover:bg-accent/50'
                                  }`}
                                >
                                  {time}
                                </button>
                                );
                              })}
                            </div>
                            {/* Custom time input for other times */}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">Outro horário:</span>
                              <label className="flex items-center gap-2 h-10 px-3 rounded-xl border border-border/50 bg-card hover:bg-accent/50 transition-colors cursor-pointer flex-1 max-w-[160px]">
                                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                                <input
                                  type="time"
                                  value={!['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00'].includes(horarioInicio) ? horarioInicio : ''}
                                  onChange={async (e) => {
                                    const newTime = e.target.value;
                                    if (simulationEventDate && newTime) {
                                      const dateObj = parseDateFromStorage(simulationEventDate);
                                      if (dateObj) {
                                        const durationForValidation = partyTimeDuration ? partyTimeDuration.substring(0, 5) : undefined;
                                        const result = validateTime(dateObj, newTime, durationForValidation);
                                        setTimeValidationResult(result);
                                        if (!result.valid) {
                                          toast.error(result.message);
                                        }
                                      }
                                    } else {
                                      setTimeValidationResult({ valid: true });
                                    }
                                    setHorarioInicio(newTime);
                                    saveEventPatch({ event_hour: newTime });
                                  }}
                                  className="bg-transparent border-none outline-none text-sm font-medium text-foreground tabular-nums w-full [&::-webkit-calendar-picker-indicator]:hidden"
                                />
                              </label>
                            </div>
                          </div>

                          {/* Time validation error - replaces success cards when invalid */}
                          {horarioInicio && !timeValidationResult.valid && (
                            <div className="p-4 rounded-xl border-2 border-destructive/50 bg-destructive/5 flex items-start gap-3 shadow-sm animate-in slide-in-from-top-2 duration-300">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-destructive/10">
                                <Clock className="h-5 w-5 text-destructive" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-destructive">
                                  ⚠️ Horário fora do atendimento
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {timeValidationResult.message}
                                </p>
                                {timeValidationResult.abertura && timeValidationResult.fechamento && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Horário de atendimento: <strong className="text-foreground">{timeValidationResult.abertura} às {timeValidationResult.fechamento}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Success info cards — when time is valid */}
                          {timeValidationResult.valid && horarioInicio && (
                            <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                              {/* Service time range — show for any menu with partyTimeDuration */}
                              {partyTimeDuration && (() => {
                                try {
                                  const [startH, startM] = horarioInicio.split(':').map(Number);
                                  const durationParts = partyTimeDuration.split(':').map(Number);
                                  const durationH = durationParts[0] || 0;
                                  const durationM = durationParts[1] || 0;
                                  let endTotalMin = (startH * 60 + startM) + (durationH * 60 + durationM);
                                  endTotalMin = endTotalMin % (24 * 60);
                                  const endH = Math.floor(endTotalMin / 60);
                                  const endM = endTotalMin % 60;
                                  const endTimeStr = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                                  return (
                                    <div className="p-4 rounded-xl border bg-muted/40 flex items-start gap-3">
                                      <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                                      <p className="text-sm text-foreground">
                                        {isServiceJourney ? 'Duração do serviço' : 'Horário da festa'}: {isServiceJourney && !durationIsMax ? 'das ' : ''}<strong>{horarioInicio}</strong> {durationIsMax ? 'com término previsto até' : 'às'} <strong>{endTimeStr}</strong>
                                      </p>
                                    </div>
                                  );
                                } catch { return null; }
                              })()}

                              {/* Arrival time */}
                              {hourArrivedData.length > 0 && (
                                <div className="p-4 rounded-xl border bg-muted/40">
                                  <div>
                                    <p className="text-sm font-medium text-foreground mb-1">
                                      {isServiceJourney ? 'Nossa chegada ao local' : 'Horário previsto de chegada do fornecedor'}
                                    </p>
                                    {hourArrivedData.map((info, idx) => {
                                      let arrivalTimeStr = '';
                                      let leadLabel = '';
                                      try {
                                        const [eventH, eventM] = horarioInicio.split(':').map(Number);
                                        const raw = (info.hourArrived || '').trim();

                                        // O valor cadastrado é SEMPRE antecedência (quanto tempo antes a equipe chega).
                                        let beforeMinutes = 0;
                                        const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
                                        const hMatch = raw.match(/(\d+)\s*h\s*(\d+)?/i);
                                        const minMatch = raw.match(/(\d+)\s*min/i);
                                        if (hhmmMatch) {
                                          beforeMinutes = (parseInt(hhmmMatch[1]) || 0) * 60 + (parseInt(hhmmMatch[2]) || 0);
                                        } else if (hMatch) {
                                          beforeMinutes = (parseInt(hMatch[1]) || 0) * 60 + (parseInt(hMatch[2]) || 0);
                                        } else if (minMatch) {
                                          beforeMinutes = parseInt(minMatch[1]) || 0;
                                        } else {
                                          const numeric = parseFloat(raw.replace(',', '.'));
                                          if (Number.isFinite(numeric)) beforeMinutes = Math.round(numeric * 60);
                                        }

                                        if (beforeMinutes > 0 && beforeMinutes < 24 * 60) {
                                          leadLabel = formatArrivalLead(
                                            `${String(Math.floor(beforeMinutes / 60)).padStart(2, '0')}:${String(beforeMinutes % 60).padStart(2, '0')}`
                                          );
                                          let totalMinutes = (eventH * 60 + eventM) - beforeMinutes;
                                          totalMinutes = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
                                          const arrH = Math.floor(totalMinutes / 60);
                                          const arrM = totalMinutes % 60;
                                          arrivalTimeStr = `${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}`;
                                        }
                                      } catch {
                                        arrivalTimeStr = '';
                                        leadLabel = '';
                                      }
                                      return (
                                        <p key={idx} className="text-sm text-muted-foreground">
                                          {isServiceJourney ? (
                                            arrivalTimeStr ? (
                                              <>
                                                {hourArrivedData.length > 1 && <><strong>{info.supplierName}</strong>: </>}
                                                Para deixar tudo preparado com carinho e atenção aos detalhes, chegaremos{' '}
                                                <strong>{leadLabel}</strong> antes do início do serviço — nossa previsão de
                                                chegada ao local é às <strong>{arrivalTimeStr}</strong>.
                                              </>
                                            ) : null
                                          ) : (
                                            <>
                                              <strong>{info.supplierName}</strong>
                                              {arrivalTimeStr ? (
                                                <>
                                                  : Chega <strong>{leadLabel}</strong> antes — previsão de chegada às{' '}
                                                  <strong>{arrivalTimeStr}</strong>
                                                </>
                                              ) : null}
                                            </>
                                          )}
                                        </p>
                                      );
                                    })}

                                  </div>
                                </div>
                              )}

                              {/* Minimal fallback confirmation when no duration or arrival data */}
                              {!partyTimeDuration && hourArrivedData.length === 0 && (
                                <div className="p-4 rounded-xl border-2 flex items-start gap-3 shadow-sm" style={{ backgroundColor: `${colors.iconColor}08`, borderColor: `${colors.iconColor}30` }}>
                                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${colors.iconColor}15` }}>
                                    <Clock className="h-5 w-5" style={{ color: colors.iconColor }} />
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold" style={{ color: colors.iconColor }}>
                                      ✅ Horário confirmado: {horarioInicio}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      Horário selecionado está dentro do atendimento do fornecedor.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Product delivery step inline if needed */}
                          {showProductDeliveryStep && Object.values(supplierTimings).some(timing => timing.trim()) && (
                            <div className="space-y-6 pt-4 border-t">
                              <div className="flex items-center gap-2">
                                <Package className="h-5 w-5" style={{ color: colors.iconColor }} />
                                <Label className="text-lg font-semibold">Data e Hora para Recebimento dos Produtos</Label>
                              </div>
                              
                              {(() => {
                                const { suppliers, totalItems } = getDeliverySuppliers();
                                return <>
                                  <div className="bg-card rounded-xl p-6 border shadow-sm">
                                    <div className="flex items-center gap-3 mb-4">
                                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.iconColor}20` }}>
                                        <Package className="h-5 w-5" style={{ color: colors.iconColor }} />
                                      </div>
                                      <div>
                                        <h3 className="font-semibold">Produtos para Entrega</h3>
                                        <p className="text-sm text-muted-foreground">Decoração e Descartáveis • {totalItems} itens</p>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4" style={{ color: colors.iconColor }} />
                                        <span className="font-medium">Data de entrega:</span>
                                        <span className="font-semibold" style={{ color: colors.iconColor }}>{calculateDeliveryDate(simulationEventDate)}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Timer className="h-4 w-4" style={{ color: colors.iconColor }} />
                                        <span className="font-medium">Horário:</span>
                                        <span className="font-semibold" style={{ color: colors.iconColor }}>9h às 18h</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="bg-card rounded-xl p-6 border shadow-sm">
                                    <div className="flex items-center gap-3 mb-4">
                                      <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                                        <MapPin className="h-5 w-5 text-accent-foreground" />
                                      </div>
                                      <div>
                                        <h3 className="font-semibold">Endereço de Entrega</h3>
                                        <p className="text-sm text-muted-foreground">Local onde os produtos serão entregues</p>
                                      </div>
                                    </div>
                                    <div className="bg-muted/30 rounded-lg p-4 mb-4">
                                      <div className="flex items-start gap-2">
                                        <Home className="h-4 w-4 text-muted-foreground mt-0.5" />
                                        <div className="text-sm">
                                          {(() => {
                                            const addressInfo = getDeliveryDisplayAddress();
                                            return <>
                                              <p className="font-medium">{addressInfo.isAlternative ? 'Endereço alternativo:' : 'Endereço da festa:'}</p>
                                              {addressInfo.recipient && <p className="text-muted-foreground font-medium">{addressInfo.recipient}</p>}
                                              <p className="text-muted-foreground">{addressInfo.formatted1}</p>
                                              <p className="text-muted-foreground">{addressInfo.formatted2}</p>
                                            </>;
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                    <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
                                      <DialogTrigger asChild>
                                        <Button type="button" variant="outline" onClick={() => { setConfirmCurrentAddress(false); setShowDeliveryModal(true); }} className="w-full flex items-center gap-2">
                                          <Truck className="h-4 w-4" /> Quero que entregue em outro endereço
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent className="max-w-2xl">
                                        <DialogHeader>
                                          <DialogTitle className="flex items-center gap-2">
                                            <User className="h-5 w-5" style={{ color: colors.iconColor }} /> Endereço para Entrega dos Produtos
                                          </DialogTitle>
                                        </DialogHeader>
                                        <div className="space-y-4">
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                              <Label className="text-sm font-medium">CEP *</Label>
                                              <div className="relative">
                                                <Input placeholder="12345-678" value={deliveryAddress.cep} onChange={e => handleDeliveryAddressChange(e.target.value)} disabled={loadingDeliveryCep} />
                                                {loadingDeliveryCep && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" style={{ color: colors.iconColor }} />}
                                              </div>
                                            </div>
                                            <div>
                                              <Label className="text-sm font-medium">Remetente *</Label>
                                              <Input placeholder="Nome de quem receberá" value={deliveryAddress.remetente} onChange={e => setDeliveryAddress(prev => ({ ...prev, remetente: e.target.value }))} />
                                            </div>
                                          </div>
                                          <div>
                                            <Label className="text-sm font-medium">Endereço *</Label>
                                            <Input placeholder="Nome da rua ou avenida" value={deliveryAddress.endereco} onChange={e => setDeliveryAddress(prev => ({ ...prev, endereco: e.target.value }))} />
                                          </div>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                              <Label className="text-sm font-medium">Número *</Label>
                                              <Input placeholder="123" value={deliveryAddress.numero} onChange={e => setDeliveryAddress(prev => ({ ...prev, numero: e.target.value }))} />
                                            </div>
                                            <div>
                                              <Label className="text-sm font-medium">Complemento</Label>
                                              <Input placeholder="Apto, casa, etc." value={deliveryAddress.complemento} onChange={e => setDeliveryAddress(prev => ({ ...prev, complemento: e.target.value }))} />
                                            </div>
                                          </div>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                              <Label className="text-sm font-medium">Cidade *</Label>
                                              <Input placeholder="Ex: São Paulo" value={deliveryAddress.cidade} onChange={e => setDeliveryAddress(prev => ({ ...prev, cidade: e.target.value }))} />
                                            </div>
                                            <div>
                                              <Label className="text-sm font-medium">Estado *</Label>
                                              <Input placeholder="Ex: SP" value={deliveryAddress.estado} onChange={e => setDeliveryAddress(prev => ({ ...prev, estado: e.target.value }))} />
                                            </div>
                                          </div>
                                          <div className="flex gap-3 pt-4">
                                            <Button type="button" variant="outline" onClick={() => setShowDeliveryModal(false)} className="flex-1">Cancelar</Button>
                                            <Button type="button" onClick={() => {
                                              if (deliveryAddress.cep && deliveryAddress.endereco && deliveryAddress.cidade && deliveryAddress.estado && deliveryAddress.numero && deliveryAddress.remetente) {
                                                setShowDeliveryModal(false);
                                                
                                              } else {
                                                toast.error("Preencha todos os campos obrigatórios");
                                              }
                                            }} className="flex-1 hover:opacity-90 transition-opacity" style={{ backgroundColor: colors.buttonColor, color: colors.buttonTextColor }}>
                                              Salvar Endereço
                                            </Button>
                                          </div>
                                        </div>
                                      </DialogContent>
                                    </Dialog>
                                  </div>

                                  <div className="p-4 rounded-lg border" style={{ backgroundColor: `${colors.iconColor}10`, borderColor: `${colors.iconColor}40` }}>
                                    <p className="text-sm">
                                      <span className="font-medium" style={{ color: colors.iconColor }}>💡 Importante:</span> Os produtos de decoração e descartáveis chegam <strong>2 dias antes da festa</strong> para que você tenha tempo de organizar tudo perfeitamente
                                    </p>
                                  </div>
                                </>;
                              })()}
                            </div>
                          )}

                        </div>
                    </div>
                  )}

                  {/* ── STEP 4: Seus Dados ── */}
                  <div className={`py-6 ${activeKey === 'data' ? 'block animate-fade-in' : 'hidden'}`}>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        {isOrderJourney
                          ? 'Para finalizar, precisamos saber quem está realizando o pedido.'
                          : 'Para finalizar, precisamos confirmar quem está organizando o evento.'}
                      </p>
                      <div className="space-y-1.5">
                        <Label className={`text-sm ${errors.nomeCompleto ? 'text-destructive' : 'text-muted-foreground'}`}>Nome Completo *</Label>
                        <Input
                          placeholder="Ex: Maria Silva"
                          value={nomeCompleto}
                          onChange={e => {
                            setNomeCompleto(e.target.value);
                            if (errors.nomeCompleto && e.target.value.trim()) setErrors((prev: any) => ({ ...prev, nomeCompleto: false }));
                          }}
                          className={errors.nomeCompleto ? 'border-destructive' : 'border-border'}
                          maxLength={120}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className={`text-sm ${errors.cpfCnpj ? 'text-destructive' : 'text-muted-foreground'}`}>CPF / CNPJ *</Label>
                          <Input
                            placeholder="000.000.000-00"
                            value={formatCpfCnpj(cpfCnpj)}
                            onChange={e => {
                              const raw = e.target.value.replace(/\D/g, '').slice(0, 14);
                              setCpfCnpj(raw);
                              if (errors.cpfCnpj && isValidCpfCnpj(raw)) setErrors((prev: any) => ({ ...prev, cpfCnpj: false }));
                            }}
                            className={errors.cpfCnpj ? 'border-destructive' : 'border-border'}
                            inputMode="numeric"
                          />
                          {errors.cpfCnpj && (
                            <p className="text-xs text-destructive">{cpfCnpjErrorMessage(cpfCnpj)}</p>
                          )}
                          {cpfCnpj.replace(/\D/g, '').length === 14 && (
                            <div className="pt-2">
                              <ClientStateRegistrationField
                                value={inscricaoEstadual}
                                onChange={(next) => {
                                  setInscricaoEstadual(next);
                                  if (errors.inscricaoEstadual && parseStateRegistration(next)) setErrors((prev: any) => ({ ...prev, inscricaoEstadual: false }));
                                }}
                                invalid={!!errors.inscricaoEstadual}
                                hint="Obrigatória para emitir a nota fiscal da empresa. Veja no cartão CNPJ; se não tiver, escolha a opção correspondente."
                              />
                            </div>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label className={`text-sm ${errors.telefoneCliente ? 'text-destructive' : 'text-muted-foreground'}`}>Telefone (com DDD) *</Label>
                          <Input
                            placeholder="(11) 99999-9999"
                            value={formatTelefone(telefoneCliente)}
                            onChange={e => {
                              const raw = e.target.value.replace(/\D/g, '').slice(0, 11);
                              setTelefoneCliente(raw);
                              if (errors.telefoneCliente && isValidTelefone(raw)) setErrors((prev: any) => ({ ...prev, telefoneCliente: false }));
                            }}
                            className={errors.telefoneCliente ? 'border-destructive' : 'border-border'}
                            inputMode="numeric"
                          />
                          {errors.telefoneCliente && telefoneCliente && (
                            <p className="text-xs text-destructive">Telefone deve ter 11 dígitos (DDD + número).</p>
                          )}
                        </div>
                      </div>
                      {!user?.email && (
                        <div className="space-y-1.5">
                          <Label className={`text-sm ${errors.emailCliente ? 'text-destructive' : 'text-muted-foreground'}`}>E-mail *</Label>
                          <Input
                            type="email"
                            placeholder="seu@email.com"
                            value={emailCliente}
                            onChange={e => {
                              setEmailCliente(e.target.value);
                              if (errors.emailCliente && isValidEmail(e.target.value)) setErrors((prev: any) => ({ ...prev, emailCliente: false }));
                            }}
                            className={errors.emailCliente ? 'border-destructive' : 'border-border'}
                            inputMode="email"
                          />
                          {errors.emailCliente && emailCliente && (
                            <p className="text-xs text-destructive">{getEmailProblem(emailCliente) ?? 'Informe um e-mail válido.'}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Navigation footer — Voltar / Próximo / Finalizar */}
                  <div className="flex items-center justify-between gap-3 pt-6 border-t border-border/40 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={activeStep === 1 ? handleBackToCart : goPrev}
                      className="rounded-lg"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1.5" />
                      Voltar
                    </Button>
                    {activeStep < totalSteps ? (
                      <Button
                        type="button"
                        onClick={goNext}
                        disabled={!canGoNext}
                        className="px-6 rounded-lg hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: colors.buttonColor, color: colors.buttonTextColor }}
                      >
                        Próximo
                      </Button>
                    ) : (
                      <Button
                        onClick={handleFestaSubmit}
                        className="px-6 rounded-lg hover:opacity-90 transition-opacity"
                        disabled={loading || loadingSimulation}
                        style={{ backgroundColor: colors.buttonColor, color: colors.buttonTextColor }}
                      >
                        {loading ? 'Processando...' : 'Ir para Pagamento'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })()}
          </div>
        </div>
      </main>
      
      {theme.isDigitalMenu ? <SupplierFooter /> : <Footer />}
    </div>;
};

export default InfosFesta;
